
// MultiMap.js (refactored UI only — same dependencies)
import React, { useState, useEffect, useMemo, useRef, useCallback, useTransition } from "react";
import { Close } from "@mui/icons-material";
import { FormGroup, FormControlLabel, Checkbox, Typography, Radio, FormControl } from "@mui/material";
import Icon from '@mui/material/Icon';
import { GoogleMap, Polyline, Marker, useJsApiLoader } from "@react-google-maps/api";
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { ScatterplotLayer, IconLayer, TextLayer } from '@deck.gl/layers';
import { HeatmapLayer, PathLayer } from 'deck.gl';
import Supercluster from 'supercluster';
import axios from "axios";
import * as _ from "underscore";
import { saveAs } from "file-saver";
import Geocode from "react-geocode";
import moment from "moment-timezone";
import { Segmented } from 'antd';
import GaugeChart from 'react-gauge-chart'
import { IoMapSharp } from "react-icons/io5";
import { PiVideoDuotone } from "react-icons/pi";
import { RiCalculatorFill, RiArticleFill, RiFileDownloadFill, RiHistoryFill, RiNotification3Fill, RiTrafficLightLine } from "react-icons/ri";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import LoadingScreen from "../../../utils/LoadingScreen";
import ProfileMenu from "../../../utils/ProfileMenu";
import { useBranding } from "../../../contexts/BrandingContext";
import { useDefectConfig } from "../../../contexts/DefectConfigContext";
import { useAuthority } from "../../../contexts/AuthorityContext";
import "./MultiMap.css";
import Expand from "../../../assets/Expand.svg";
import trafficSignalIcon from "../../../assets/traffic-jam.svg";
import selectedPointIcon from "../../../assets/road-engineer-option2-removebg.svg";
import { FcPrevious, FcNext, FcVideoCall, FcNoVideo, FcCamera } from "react-icons/fc";
import { tr } from "@faker-js/faker";
import { mapLabel } from "../../../utils/labelMapping";
import BHILWARA_TRAFFIC_POINTS from "./bhilwaraTrafficPoints";
import BHILWARA_DEFECT_POINTS from "./bhilwaraDefectPoints";
import {
  _memCache,
  MEM_CACHE_TTL,
  getGisUserEmail as _gisGetUserEmail,
} from '../../../utils/gisMarkerCache';
import {
  ASSET_INVENTORY_CONFIG,
  ASSET_INVENTORY_COLOR,
  isAssetInventoryName,
} from '../FinalReportDashboard/subCategoriesConfig';

const libraries = ['places'];

// deck.gl v9 / luma.gl v9 require a WebGL2 context (WebGL1 support was dropped).
// If the browser can't create one — e.g. hardware acceleration disabled or a
// blocklisted GPU driver — luma.gl throws during device init and takes down the
// whole page. Probe for WebGL2 up front so we can skip the deck.gl overlay and
// render the plain Google Map with a notice instead of crashing.
const isWebGL2Available = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    return !!gl;
  } catch {
    return false;
  }
};

// ═══════════════════════════════════════════════════════════════════
// Khammam Municipal Corporation — All 50 Wards with center coords
// Each marker on the map is assigned to its nearest ward instantly.
// ═══════════════════════════════════════════════════════════════════
const KHAMMAM_WARDS = [
  { ward: 1, name: "Ward 1 - Raghunadha Palem", lat: 17.2690, lng: 80.1280 },
  { ward: 2, name: "Ward 2 - Saradhi Nagar", lat: 17.2670, lng: 80.1350 },
  { ward: 3, name: "Ward 3 - Mamilla Gadaem", lat: 17.2620, lng: 80.1360 },
  { ward: 4, name: "Ward 4 - Bhimas Nagar", lat: 17.2610, lng: 80.1450 },
  { ward: 5, name: "Ward 5 - UPH Colony", lat: 17.2595, lng: 80.1490 },
  { ward: 6, name: "Ward 6 - Prashanthi Nagar", lat: 17.2650, lng: 80.1520 },
  { ward: 7, name: "Ward 7 - Tekulapalli", lat: 17.2620, lng: 80.1600 },
  { ward: 8, name: "Ward 8 - Indira Nagar", lat: 17.2570, lng: 80.1580 },
  { ward: 9, name: "Ward 9 - Sri Nagar Colony", lat: 17.2530, lng: 80.1610 },
  { ward: 10, name: "Ward 10 - Madhura Nagar", lat: 17.2490, lng: 80.1620 },
  { ward: 11, name: "Ward 11 - Raghavaiah Nagar", lat: 17.2510, lng: 80.1550 },
  { ward: 12, name: "Ward 12 - Sathupalii Road", lat: 17.2530, lng: 80.1500 },
  { ward: 13, name: "Ward 13 - Mamatha Hospital Area", lat: 17.2500, lng: 80.1470 },
  { ward: 14, name: "Ward 14 - VDO's Colony", lat: 17.2580, lng: 80.1440 },
  { ward: 15, name: "Ward 15 - NST Road", lat: 17.2570, lng: 80.1380 },
  { ward: 16, name: "Ward 16 - Khanapuram Haveli", lat: 17.2520, lng: 80.1350 },
  { ward: 17, name: "Ward 17 - Khammam Fort", lat: 17.2480, lng: 80.1370 },
  { ward: 18, name: "Ward 18 - Lakaram Tank Bund", lat: 17.2440, lng: 80.1430 },
  { ward: 19, name: "Ward 19 - Munawarpet", lat: 17.2420, lng: 80.1380 },
  { ward: 20, name: "Ward 20 - Pakabanda Bazar", lat: 17.2380, lng: 80.1450 },
  { ward: 21, name: "Ward 21 - Sai Nagar", lat: 17.2400, lng: 80.1510 },
  { ward: 22, name: "Ward 22 - Ballepalli", lat: 17.2360, lng: 80.1530 },
  { ward: 23, name: "Ward 23 - Masjid-E-Mustafa Nagar", lat: 17.2350, lng: 80.1400 },
  { ward: 24, name: "Ward 24 - Shivalayam Area", lat: 17.2310, lng: 80.1420 },
  { ward: 25, name: "Ward 25 - Rajendra Nagar", lat: 17.2340, lng: 80.1320 },
  { ward: 26, name: "Ward 26 - Prakash Nagar", lat: 17.2420, lng: 80.1280 },
  { ward: 27, name: "Ward 27 - Sri Ayyappa Swamy Area", lat: 17.2380, lng: 80.1300 },
  { ward: 28, name: "Ward 28 - Annapurna Colony", lat: 17.2360, lng: 80.1250 },
  { ward: 29, name: "Ward 29 - Gandhi Nagar", lat: 17.2450, lng: 80.1230 },
  { ward: 30, name: "Ward 30 - Pumping Well Road", lat: 17.2430, lng: 80.1170 },
  { ward: 31, name: "Ward 31 - Basavi Gardens", lat: 17.2500, lng: 80.1190 },
  { ward: 32, name: "Ward 32 - SV Market Area", lat: 17.2530, lng: 80.1250 },
  { ward: 33, name: "Ward 33 - DMart Area", lat: 17.2580, lng: 80.1200 },
  { ward: 34, name: "Ward 34 - Nava Nagar", lat: 17.2540, lng: 80.1310 },
  { ward: 35, name: "Ward 35 - Balaji Nagar", lat: 17.2550, lng: 80.1140 },
  { ward: 36, name: "Ward 36 - Church Junction", lat: 17.2420, lng: 80.1340 },
  { ward: 37, name: "Ward 37 - Overhead Tank Area", lat: 17.2430, lng: 80.1280 },
  { ward: 38, name: "Ward 38 - Jyothi Nagar", lat: 17.2470, lng: 80.1140 },
  { ward: 39, name: "Ward 39 - Bhuranpuram", lat: 17.2630, lng: 80.1410 },
  { ward: 40, name: "Ward 40 - Sahakar Nagar", lat: 17.2560, lng: 80.1480 },
  { ward: 41, name: "Ward 41 - Stambadri Lakshmi Area", lat: 17.2590, lng: 80.1360 },
  { ward: 42, name: "Ward 42 - Sammakka Colony", lat: 17.2630, lng: 80.1180 },
  { ward: 43, name: "Ward 43 - Wyra Road", lat: 17.2660, lng: 80.1250 },
  { ward: 44, name: "Ward 44 - Old Bus Stand Area", lat: 17.2580, lng: 80.1300 },
  { ward: 45, name: "Ward 45 - Sathupalli Road South", lat: 17.2460, lng: 80.1550 },
  { ward: 46, name: "Ward 46 - Green Honda Area", lat: 17.2520, lng: 80.1650 },
  { ward: 47, name: "Ward 47 - Park Area South", lat: 17.2320, lng: 80.1530 },
  { ward: 48, name: "Ward 48 - Vemikya Nagar", lat: 17.2370, lng: 80.1470 },
  { ward: 49, name: "Ward 49 - Hospital Road", lat: 17.2470, lng: 80.1580 },
  { ward: 50, name: "Ward 50 - Khammam District Court", lat: 17.2590, lng: 80.1530 },
];

// Fast Haversine distance in km between two points
const _haversineKm = (lat1, lng1, lat2, lng2) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Given a lat/lng, find the nearest Khammam ward
const findNearestWard = (lat, lng) => {
  let minDist = Infinity;
  let nearest = KHAMMAM_WARDS[0];
  for (const w of KHAMMAM_WARDS) {
    const d = _haversineKm(lat, lng, w.lat, w.lng);
    if (d < minDist) {
      minDist = d;
      nearest = w;
    }
  }
  return nearest.name;
};

// Label-to-color map for inference_info label-based point coloring.
// When defect_state is "none", the dominant color across all labels on a frame is used.
const LABEL_COLOR_MAP = {
  'pavement damage (severe)': '#DC3C3C',
  'potholes': '#DC3C3C',
  'pothole': '#DC3C3C',
  'settlement': '#DC3C3C',
  'rutting': '#DC3C3C',
  'shoulder edge drop': '#DC3C3C',
  'damaged sign boards / sign structures': '#DC3C3C',
  'damaged sign boards': '#DC3C3C',
  'damaged highway lights': '#DC3C3C',
  'non-functional highway lights': '#DC3C3C',
  'missing assets (street lights)': '#DC3C3C',
  'damaged crash barriers': '#DC3C3C',
  'damaged metal beam crash barrier': '#DC3C3C',
  'damaged pedestrian guard rail': '#DC3C3C',
  'damaged kerb': '#DC3C3C',
  'damaged drain cover slabs': '#DC3C3C',
  'missing drain cover slabs': '#DC3C3C',
  'damaged blinkers': '#DC3C3C',
  'damaged attenuators': '#DC3C3C',
  'damaged delineators': '#DC3C3C',
  'damaged anti-glare': '#DC3C3C',
  'damaged hazard markers': '#DC3C3C',
  'reduced visibility due to overgrowth': '#DC3C3C',
  'damaged rumble strips': '#DC3C3C',
  'faded pavement marking': '#DC3C3C',
  'pavement marking - poor visibility (night)': '#DC3C3C',
  'work zone - inadequate barricading': '#DC3C3C',
  'work zone - poor diversion arrangement / condition': '#DC3C3C',
  'missing assets (guard rails)': '#DC3C3C',
  'median separator damaged': '#DC3C3C',
  'unauthorized median opening': '#DC3C3C',
  'stripping / delamination': '#E87A00',
  'cracks': '#E87A00',
  'crack': '#E87A00',
  'cracking': '#E87A00',
  'water stagnation': '#E87A00',
  'shoulder unevenness': '#E87A00',
  'shoulder rain cut': '#E87A00',
  'signage - poor visibility (day)': '#E87A00',
  'signage - poor visibility (night)': '#E87A00',
  'damaged footpath tiles / paver blocks': '#E87A00',
  'damaged footpath': '#E87A00',
  'damaged road studs': '#E87A00',
  'bus bay - damaged shelters': '#E87A00',
  'truck lay by - damaged shelters': '#E87A00',
  'missing plants / irregular gaps (median)': '#E87A00',
  'deteriorated or damaged plants (median)': '#E87A00',
  'unauthorized hoardings': '#E87A00',
  'pavement marking - poor visibility (day)': '#E87A00',
  'work zone - inadequate signboard visibility': '#E87A00',
  'faded painting concrete crash barrier': '#E87A00',
  'barriers - faded painting guard rails': '#E87A00',
  'faded kerb painting': '#E87A00',
  'median separator paint faded': '#E87A00',
  'general encroachment': '#E87A00',
  'unsealed roads': '#C8C800',
  'pavement joint': '#C8C800',
  'road studs - poor visibility (day)': '#C8C800',
  'road studs - poor visibility (night)': '#C8C800',
  'bus bay - faded markings': '#C8C800',
  'bus bay - damaged signages': '#C8C800',
  'truck lay by - faded markings': '#C8C800',
  'truck lay by - damaged signages': '#C8C800',
  'manhole': '#C8C800',
  'illegal parking': '#C8C800',
  'shoulder - vegetation growth': '#C8C800',
  'excessive plantation growth': '#C8C800',
  'unauthorized advertisement board': '#C8C800',
  'cleanliness - litter': '#C8C800',
  'cleanliness - debris': '#C8C800',
};

// Normalize a stored label (snake_case / hyphens) to plain lowercase for map lookup
const _normLabelForColor = (label) =>
  label.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();

// Return the mapped color for a raw inference_info label, or null if not found
const _getLabelColor = (rawLabel) => {
  const norm = _normLabelForColor(rawLabel);
  if (LABEL_COLOR_MAP[norm]) return LABEL_COLOR_MAP[norm];
  // Partial match fallback (handles "pothole" vs "potholes", prefix/suffix variants)
  for (const [key, color] of Object.entries(LABEL_COLOR_MAP)) {
    if (norm.includes(key) || key.includes(norm)) return color;
  }
  return null;
};

// ── Bhilwara-specific color logic ────────────────────────────────────────────
// Point color depends on BOTH defect label AND severity for bhilwaraUser@roadvision.ai

// Severity-dependent defects: color varies by severity level
const BHILWARA_SEVERITY_COLOR = {
  'potholes': { high: '#DC3C3C', medium: '#E87A00', low: '#C8C800' },
  'pothole':  { high: '#DC3C3C', medium: '#E87A00', low: '#C8C800' },
  'cracking': { high: '#E87A00', medium: '#C8C800', low: '#C8C800' },
  'crack':    { high: '#E87A00', medium: '#C8C800', low: '#C8C800' },
  'cracks':   { high: '#E87A00', medium: '#C8C800', low: '#C8C800' },
  'rutting':  { high: '#DC3C3C', medium: '#E87A00', low: '#C8C800' },
};

// Fixed-color defects: color is determined by label alone, ignoring severity
const BHILWARA_FIXED_COLOR = {
  'patching':                                              '#C8C800',
  'water stagnation':                                      '#E87A00',
  'rain cuts':                                             '#E87A00',
  'rain cuts shoulders':                                   '#E87A00',
  'shoulder rain cut':                                     '#E87A00',
  'edge drop uneven shoulders':                            '#DC3C3C',
  'shoulder edge drop':                                    '#DC3C3C',
  'shoulder unevenness':                                   '#DC3C3C',
  'damaged sign boards structures':                        '#DC3C3C',
  'damaged sign boards':                                   '#DC3C3C',
  'signage visibility day night':                          '#E87A00',
  'signage poor visibility (day)':                         '#E87A00',
  'signage poor visibility (night)':                       '#E87A00',
  'damaged lights':                                        '#DC3C3C',
  'damaged highway lights':                                '#DC3C3C',
  'non functional lights':                                 '#DC3C3C',
  'missing assets (street lights)':                        '#DC3C3C',
  'crash barriers guard rails damaged':                    '#DC3C3C',
  'damaged crash barriers':                                '#DC3C3C',
  'damaged metal beam crash barrier':                      '#DC3C3C',
  'damaged pedestrian guard rail':                         '#DC3C3C',
  'faded painting barrier guard rail':                     '#E87A00',
  'faded painting concrete crash barrier':                 '#E87A00',
  'barriers faded painting guard rails':                   '#E87A00',
  'damaged kerb faded paint':                              '#DC3C3C',
  'damaged kerb':                                          '#DC3C3C',
  'faded kerb painting':                                   '#DC3C3C',
  'damaged missing cover slabs':                           '#DC3C3C',
  'damaged drain cover slabs':                             '#DC3C3C',
  'missing drain cover slabs':                             '#DC3C3C',
  'damaged footpath':                                      '#E87A00',
  'damaged footpath tiles / paver blocks':                 '#E87A00',
  'damaged blinkers attenuators delineators anti glare':   '#DC3C3C',
  'damaged blinkers':                                      '#DC3C3C',
  'damaged attenuators':                                   '#DC3C3C',
  'damaged delineators':                                   '#DC3C3C',
  'damaged anti-glare':                                    '#DC3C3C',
  'damaged road studs rumble strips hazard markers':       '#E87A00',
  'damaged road studs':                                    '#E87A00',
  'damaged rumble strips':                                 '#E87A00',
  'damaged hazard markers':                                '#E87A00',
  'road stud rumble strip visibility day night':           '#C8C800',
  'road studs poor visibility (day)':                      '#C8C800',
  'road studs poor visibility (night)':                    '#C8C800',
  'damaged bus shelters faded markings damaged signages':  '#E87A00',
  'damaged bus shelters':                                  '#E87A00',
  'bus bay damaged shelters':                              '#E87A00',
  'truck lay by damaged shelters':                         '#E87A00',
  'bus bay faded markings':                                '#C8C800',
  'truck lay by faded markings':                           '#C8C800',
  'missing plants irregular gaps':                         '#E87A00',
  'missing plants / irregular gaps (median)':              '#E87A00',
  'deteriorated damaged plants':                           '#E87A00',
  'deteriorated or damaged plants (median)':               '#E87A00',
  'reduced visibility due to plant growth':                '#DC3C3C',
  'reduced visibility due to overgrowth':                  '#DC3C3C',
  'unauthorized signboards hoardings':                     '#E87A00',
  'unauthorized hoardings':                                '#E87A00',
  'holding advertisement boards':                          '#C8C800',
  'unauthorized advertisement board':                      '#C8C800',
  'faded pavement marking':                                '#DC3C3C',
  'pavement marking poor visibility (night)':              '#DC3C3C',
  'pavement marking visibility day night':                 '#E87A00',
  'pavement marking poor visibility (day)':                '#E87A00',
  'missing poor safety measures':                          '#DC3C3C',
  'work zone inadequate barricading':                      '#DC3C3C',
  'work zone inadequate signboard visibility':             '#DC3C3C',
  'inappropriate diversions':                              '#DC3C3C',
  'work zone poor diversion arrangement / condition':      '#DC3C3C',
  'encroachments illegal parking':                         '#E87A00',
  'general encroachment':                                  '#E87A00',
  'illegal parking':                                       '#E87A00',
  'unauthorized median openings':                          '#DC3C3C',
  'unauthorized median opening':                           '#DC3C3C',
  'vegetation growth shoulders':                           '#C8C800',
  'excessive plantation growth':                           '#C8C800',
  'overall cleanliness':                                   '#C8C800',
  'cleanliness litter':                                    '#C8C800',
  'cleanliness debris':                                    '#C8C800',
};

// Normalize a raw severity value to 'high' | 'medium' | 'low' | 'none'
const _normSeverity = (raw) => {
  const s = (raw || '').toLowerCase().trim();
  if (s === 'high' || s === 'h' || s === '3') return 'high';
  if (s === 'medium' || s === 'm' || s === '2') return 'medium';
  if (s === 'low' || s === 'l' || s === '1') return 'low';
  return 'none';
};

// Keyword fallback — catches backend label variants that don't substring-match the
// BHILWARA_FIXED_COLOR keys (e.g. "vegetation_growth_on_shoulders" vs "vegetation_growth_shoulders").
// Orange is checked BEFORE Red so "pavement marking visibility" labels don't fall through to red.
const _BHW_ORANGE_KW = [
  'water stagnation', 'stagnation',
  'rain cut',
  'signage visibility', 'visibility of signage', 'sign visibility',
  'faded painting', 'barrier paint',
  'footpath', 'paver block', 'footway',
  'damaged road stud', 'damaged rumble', 'hazard marker',
  'bus shelter', 'damaged bus',
  'missing plant', 'irregular gap',
  'deteriorated plant', 'damaged plant',
  'unauthorized signboard', 'unauthorized hoarding',
  'marking visibility', 'pavement marking visibility',
  'encroachment', 'illegal parking',
];
const _BHW_RED_KW = [
  'edge drop', 'uneven shoulder', 'unevenness',
  'damaged sign board', 'sign structure', 'sign board damage',
  'damaged light', 'highway light',
  'non functional', 'non-functional',
  'crash barrier', 'guard rail', 'guardrail',
  'damaged kerb', 'kerb faded', 'faded kerb', 'kerb paint',
  'drain cover', 'cover slab',
  'blinker', 'attenuator', 'delineator', 'anti-glare', 'anti glare',
  'sight distance', 'reduced visibility due to plant', 'overgrowth',
  'pavement marking', // generic — catches "pavement marking" alone after orange check passes
  'work zone', 'barricading',
  'inappropriate diversion',
  'median opening', 'unauthorized median',
];
const _BHW_YELLOW_KW = [
  'patching',
  'vegetation growth', 'shoulder vegetation', 'vegetation on shoulder',
  'studs & markers', 'studs and markers', 'stud visibility',
  'marker visibility', 'rumble strip visibility', 'road stud visibility',
  'bus bay marking', 'bus bay faded', 'faded bus bay',
  'truck lay by marking', 'faded truck lay',
  'advertisement board', 'advertisement hoarding', 'holding board',
  'cleanliness', 'litter', 'debris',
];

// Return the Bhilwara color for a single inference_info item (label + severity)
const _getBhilwaraLabelColor = (rawLabel, rawSeverity) => {
  const norm = _normLabelForColor(rawLabel);
  const sev = _normSeverity(rawSeverity);

  // 1. Exact match in severity-dependent map
  if (BHILWARA_SEVERITY_COLOR[norm]) {
    const colors = BHILWARA_SEVERITY_COLOR[norm];
    return colors[sev] || colors.low || '#C8C800';
  }
  // 2. Partial match in severity-dependent map
  for (const [key, colors] of Object.entries(BHILWARA_SEVERITY_COLOR)) {
    if (norm.includes(key) || key.includes(norm)) {
      return colors[sev] || colors.low || '#C8C800';
    }
  }
  // 3. Exact match in fixed-color map
  if (BHILWARA_FIXED_COLOR[norm]) return BHILWARA_FIXED_COLOR[norm];
  // 4. Partial match in fixed-color map
  for (const [key, color] of Object.entries(BHILWARA_FIXED_COLOR)) {
    if (norm.includes(key) || key.includes(norm)) return color;
  }
  // 5. Keyword fallback — handles backend label variants not caught above
  //    Orange checked before Red to avoid ambiguous "pavement marking" labels → red
  if (_BHW_ORANGE_KW.some(kw => norm.includes(kw))) return '#E87A00';
  if (_BHW_RED_KW.some(kw => norm.includes(kw))) return '#DC3C3C';
  if (_BHW_YELLOW_KW.some(kw => norm.includes(kw))) return '#C8C800';

  return null;
};

// Derive the dominant Bhilwara point color from a frame's inference_info array
const _getBhilwaraMarkerColor = (inferenceInfo) => {
  if (!inferenceInfo || inferenceInfo.length === 0) return '#00BB00';
  const COLOR_PRIORITY = { '#DC3C3C': 3, '#E87A00': 2, '#C8C800': 1 };
  const colorCounts = {};
  inferenceInfo.forEach(info => {
    let color = _getBhilwaraLabelColor(info.label || '', info.severity || '');
    if (!color) {
      const sev = _normSeverity(info.severity || '');
      if (sev === 'high') color = '#DC3C3C';
      else if (sev === 'medium') color = '#E87A00';
      else color = '#C8C800';
    }
    colorCounts[color] = (colorCounts[color] || 0) + 1;
  });
  const entries = Object.entries(colorCounts);
  if (entries.length > 0) {
    return entries.sort(
      (a, b) => b[1] - a[1] || (COLOR_PRIORITY[b[0]] || 0) - (COLOR_PRIORITY[a[0]] || 0)
    )[0][0];
  }
  return '#C8C800';
};

// ── Bhilwara filter panel: static label list and category config ──────────────
// Ordered list of display labels for bhilwaraUser's defect filter sidebar.
// Severity variants are separate checkboxes filtering by label + severity.
// Canonical Bhilwara label list — categorized below in BHILWARA_SUB_CATEGORIES_CONFIG.
// Hyphen-minus (not en-dash) so these match Label-Studio export labels after the
// normalizer at line ~805 converts en-dashes to hyphens.
const BHILWARA_STATIC_LABELS = [
  // Roadway Surface — Pavement Condition
  'Potholes', 'Cracking', 'Rutting', 'Patching', 'Pavement Damage',
  'Unsealed Road', 'Corner Break (Concrete)', 'Faulting at Joints',
  'Blow-up (Buckling)',
  // Roadway Surface — Shoulder Condition
  'Shoulder - Edge Drop', 'Shoulder - Unevenness', 'Shoulder - Vegetation Growth',
  // Asset Management — Kerb & Median Condition
  'Damaged Kerb', 'Faded Kerb Painting',
  'Median Separator Damaged', 'Median Separator Paint Faded',
  // Asset Management — Plantation / Landscaping
  'Reduced Visibility Due to Plantation Growth',
  'Missing Plants / Irregular Gaps (Median)',
  'Deteriorated or Damaged Plants (Median)',
  'Excessive Plantation Growth',
  // Asset Management — Drainage
  'Damaged Drain Cover Slabs', 'Missing Drain Cover Slabs',
  'Manhole Cover', 'Water Stagnation',
  // Asset Management — Footpath
  'Damaged Footpath Tiles / Paver Blocks',
  // Asset Management — Crash Barriers & Guard Rails
  'Damaged Crash Barriers',
  'Damaged (MBCB) Metal Beam Crash Barrier',
  'Damaged (PGR) Pedestrian Guard Rail',
  'Faded Painting Concrete Crash (CC) Barrier',
  'Barriers - Faded Painting Guard Rails',
  // Asset Management — Signages
  'Damaged Sign Boards / Sign Structures',
  'Signage - Poor Visibility (Day)', 'Signage - Poor Visibility (Night)',
  // Asset Management — Safety Devices
  'Damaged Blinkers', 'Damaged Delineators',
  // Asset Management — Road Studs & Markers
  'Damaged Road Studs', 'Damaged Rumble Strips', 'Damaged Hazard Markers',
  // Asset Management — Encroachment
  'Unauthorized Median Openings', 'Unauthorized Hoardings',
  'Illegal Parking', 'General Encroachments',
  // Asset Management — Bus Bay & Truck Lay-By
  'Bus Bay - Damaged Shelters', 'Bus Bay - Faded Markings',
  'Bus Bay - Damaged Signages',
  // Asset Management — Cleanliness
  'Cleanliness (Litter)', 'Cleanliness (Debris)',
  // Road Safety — Pavement Marking
  'Faded Pavement Markings',
  // Road Safety — Highway Lighting
  'Missing Road Lights', 'Damaged Road Lights', 'Non-Functional Road Lights',
  // Road Safety — Work Zone Safety
  'Work Zone - Inadequate Signboard Visibility',
  'Work Zone - Inadequate Barricading',
  'Work Zone - Poor Diversion Arrangement / Condition',
];

// Names must be exact lowercase of BHILWARA_STATIC_LABELS so the cbByLabel lookup
// in categorizedCheckboxes() succeeds.
const BHILWARA_SUB_CATEGORIES_CONFIG = {
  pavement: {
    pavement_condition: {
      label: 'Pavement Condition',
      names: [
        'potholes', 'cracking', 'rutting', 'patching', 'pavement damage',
        'unsealed road', 'corner break (concrete)', 'faulting at joints',
        'blow-up (buckling)',
        'water_stagnation',
      ],
    },
    shoulder_condition: {
      label: 'Shoulder Condition',
      names: ['shoulder - edge drop', 'shoulder - unevenness', 'shoulder - vegetation growth',
        'unevenness_/_edge_drop_of_shoulder', 'rain_cuts'],
    },
  },
  assets: {
    kerb_median: {
      label: 'Kerb & Median Condition',
      names: ['damaged kerb', 'faded kerb painting', 'median separator damaged', 'median separator paint faded',
        'damaged_kerb_/_faded_kerb_painting'],
    },
    plantation: {
      label: 'Plantation / Landscaping',
      names: [
        'reduced visibility due to plantation growth',
        'missing plants / irregular gaps (median)',
        'deteriorated or damaged plants (median)',
        'excessive plantation growth',
        'damaged_/_deteriorated_plants', 'missing_plants_/_irregular_gaps', 'reduced_sight_distance',
      ],
    },
    drainage: {
      label: 'Drainage',
      names: ['damaged drain cover slabs', 'missing drain cover slabs', 'manhole cover', 'water stagnation',
        'damaged_/_missing_drain_cover_slabs'],
    },
    footpath: {
      label: 'Footpath',
      names: ['damaged footpath tiles / paver blocks',
        'damaged_footpath'],
    },
    crash_barriers: {
      label: 'Crash Barriers & Guard Rails',
      names: [
        'damaged crash barriers',
        'damaged (mbcb) metal beam crash barrier',
        'damaged (pgr) pedestrian guard rail',
        'faded painting concrete crash (cc) barrier',
        'barriers - faded painting guard rails',
        'damaged_crash_barriers_/_guardrails', 'faded_painting_on_barriers',
      ],
    },
    signages: {
      label: 'Signages',
      names: ['damaged sign boards / sign structures', 'signage - poor visibility (day)', 'signage - poor visibility (night)',
        'damaged_sign_boards_/_sign_structures', 'visibility_of_signages'],
    },
    safety_devices: {
      label: 'Safety Devices',
      names: ['damaged blinkers', 'damaged delineators'],
    },
    road_studs: {
      label: 'Road Studs & Markers',
      names: ['damaged road studs', 'damaged rumble strips', 'damaged hazard markers',
        'missing/damaged_road_studs_/_rumble_strips_/_hazard_markers', 'visibility_of_studs_&_markers'],
    },
    encroachment: {
      label: 'Encroachment',
      names: ['unauthorized median openings', 'unauthorized hoardings', 'illegal parking', 'general encroachments',
        'unauthorized_median_openings', 'unauthorized_signboards_/_hoardings',
        'holding_/_advertisement_boards', 'encroachment', 'encroachments/_illegal_parking'],
    },
    bus_bay: {
      label: 'Bus Bay & Truck Lay-By',
      names: ['bus bay - damaged shelters', 'bus bay - faded markings', 'bus bay - damaged signages'],
    },
    cleanliness: {
      label: 'Cleanliness',
      names: ['cleanliness (litter)', 'cleanliness (debris)',
        'cleanliness', 'vegetation_growth_on_shoulders'],
    },
  },
  roadSafety: {
    pavement_markings: {
      label: 'Pavement Marking',
      names: ['faded pavement markings',
        'pavement_marking', 'pavement_marking_visibility'],
    },
    highway_lighting: {
      label: 'Highway Lighting',
      names: ['missing road lights', 'damaged road lights', 'non-functional road lights',
        'highway_lights_damaged'],
    },
    work_zone: {
      label: 'Work Zone Safety',
      names: [
        'work zone - inadequate signboard visibility',
        'work zone - inadequate barricading',
        'work zone - poor diversion arrangement / condition',
        'work_zone_safety', 'inappropriate_diversions',
      ],
    },
  },
};

// In-memory + sessionStorage GIS marker caches live in utils/gisMarkerCache
// so AuthContext can wipe them on logout without pulling in the map component.
// Both layers are bucketed by user email.

// Snap GPS survey points to nearest road centerline using Google Roads API.
// Runs in background batches of 100 — map shows raw coords first, then
// silently updates to snapped coords. Falls back gracefully if API not enabled.
const snapMarkersToRoads = async (markers) => {
  const apiKey = process.env.REACT_APP_GOOGLE_KEYS;
  if (!apiKey || markers.length === 0) return markers;

  const BATCH_SIZE = 100;
  const snappedCoords = new Map(); // global index → { latitude, longitude }

  const batches = [];
  for (let i = 0; i < markers.length; i += BATCH_SIZE) {
    batches.push({ startIdx: i, items: markers.slice(i, i + BATCH_SIZE) });
  }

  await Promise.all(batches.map(async ({ startIdx, items }) => {
    const points = items.map(m => `${m.latitude},${m.longitude}`).join('|');
    try {
      const res = await fetch(
        `https://roads.googleapis.com/v1/nearestRoads?points=${encodeURIComponent(points)}&key=${apiKey}`
      );
      const data = await res.json();
      if (data.snappedPoints) {
        data.snappedPoints.forEach(sp => {
          snappedCoords.set(startIdx + (sp.originalIndex ?? 0), {
            latitude: sp.location.latitude,
            longitude: sp.location.longitude,
          });
        });
      }
    } catch (e) {
      console.warn(`Road snapping failed for batch starting at ${startIdx}:`, e);
    }
  }));

  if (snappedCoords.size === 0) return markers;
  return markers.map((m, i) => {
    const snapped = snappedCoords.get(i);
    return snapped ? { ...m, ...snapped } : m;
  });
};

const MultiMap = () => {
  const { brandingSettings } = useBranding();
  const { isLabelEnabled } = useDefectConfig();
  const { authorities: AUTHORITY_OPTIONS_FROM_CTX } = useAuthority();
  Geocode.setApiKey(process.env.REACT_APP_GOOGLE_KEYS);
  const isKhammamUser = (JSON.parse(window.localStorage.getItem("user"))?.email || "").toLowerCase() === "khammam@roadvision.ai";
  const isBhilwaraUser = (JSON.parse(window.localStorage.getItem("user"))?.email || "").toLowerCase() === "bhilwarauser@roadvision.ai";

  // --- state (kept same as original) ---
  const [markers, setMarkers] = useState([]);
  const [open, setOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(13);
  const [selectedRadio, setSelectedRadio] = useState("ai_analyzed");
  const [polylinePaths, setPolylinePaths] = useState([]);
  const [allInferenceList, setInferenceList] = useState([]);
  const [info, setInfo] = useState({});
  const [selectedPolylineData, setSelectedPolylineData] = useState(null);
  const [isPolylineModalOpen, setPolylineModalOpen] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState({ latitude: null, longitude: null });
  const [center, setCenter] = useState({ lat: 17.2473, lng: 80.1514 }); // Khammam center
  const [hasInitialFit, setHasInitialFit] = useState(false); // tracks if we've done initial fitBounds
  const [geolocated, setGeolocated] = useState(false); // tracks if user location was used for initial center
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [autoPlayIntervalId, setAutoPlayIntervalId] = useState(null);
  const [isRoad, setIsRoad] = useState(false);
  const [imageSrc, setImageSrc] = useState({ og_src: "", ai_src: "" });
  const [backupMarkerState, setBackupMarkerState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPending, startTransition] = useTransition();
  const [isMapReady, setIsMapReady] = useState(false);
  const [webglUnavailable, setWebglUnavailable] = useState(false); // true when WebGL2 missing — deck.gl layers disabled
  const [markersLoaded, setMarkersLoaded] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [mapStyleMode, setMapStyleMode] = useState('white'); // 'white' or 'dark'
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [mapDisplayMode, setMapDisplayMode] = useState('defects'); // 'defects' | 'heatmap' | 'road_condition'
  const [trafficCongestionData, setTrafficCongestionData] = useState([]); // traffic congestion points from road data
  const [showTrafficCongestion, setShowTrafficCongestion] = useState(false); // filter toggle for traffic congestion layer
  const [selectedTrafficPoint, setSelectedTrafficPoint] = useState(null); // clicked traffic congestion point for InfoWindow
  const [panelWidth, setPanelWidth] = useState(360); // right panel width in px
  const panelDragRef = useRef(null); // tracks drag state for resize handle
  const [isPanelDragging, setIsPanelDragging] = useState(false); // locks cursor during panel resize
  const [isImageFullView, setIsImageFullView] = useState(false); // fullscreen image lightbox

  // preserved references
  var polylineSegments = [];
  const [searchParams, setSearchParams] = useSearchParams();
  const uid_list = useMemo(() => JSON.parse(decodeURIComponent(searchParams.get("listOfIds") || "[]")), [searchParams]);

  // Chainage-segment focus: when opened from the chainage report's "Map" action,
  // the URL carries the segment's start/end coordinates. We build a padded bounding
  // box and treat it as a location filter so only that segment's defect points show
  // (and the map zooms to it) instead of the whole road. Null = no segment focus.
  const chainageBounds = useMemo(() => {
    const s = searchParams.get("chainageStart");
    const e = searchParams.get("chainageEnd");
    if (!s || !e) return null;
    const [sLat, sLng] = s.split(",").map(Number);
    const [eLat, eLng] = e.split(",").map(Number);
    if (![sLat, sLng, eLat, eLng].every(Number.isFinite)) return null;
    const PAD = 0.0004; // ~40m padding to include points that bow outside a curved segment
    return {
      minLat: Math.min(sLat, eLat) - PAD,
      maxLat: Math.max(sLat, eLat) + PAD,
      minLng: Math.min(sLng, eLng) - PAD,
      maxLng: Math.max(sLng, eLng) + PAD,
    };
  }, [searchParams]);
  const imageRef = useRef(null);
  const zoomTimeoutRef = useRef(null);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const isFilteringRef = useRef(false); // prevents filter re-trigger loop
  const defectTypesAppliedRef = useRef(false); // prevents re-applying URL defect filter

  // Derive the annotated-image path from a marker. Handles three cases:
  //   1. Synthetic frames already carry the full `ai_file` GCS path.
  //   2. Bhilwara backend frames use sibling folders with `_pre_annotated` /
  //      `_post_annotated` suffixes — the legacy `/frames/→/predict/` rename
  //      doesn't apply.
  //   3. Everything else: the long-standing `/frames/→/predict/` convention.
  const deriveAiFile = (marker) => {
    if (marker?.ai_file) return marker.ai_file;
    const og = marker?.og_file || '';
    if (og.startsWith('bhilwara_rotated_frames_pre_new/')) {
      return og.replace('bhilwara_rotated_frames_pre_new/', 'bhilwara_rotated_frames_pre_new_pre_annotated/');
    }
    if (og.startsWith('bhilwara_rotated_frames_post_new/')) {
      return og.replace('bhilwara_rotated_frames_post_new/', 'bhilwara_rotated_frames_post_new_post_annotated/');
    }
    return og.replace('/frames/', '/predict/');
  };

  // --- existing helper functions & effects (kept intact) ---
  const updatePolylinePaths = useCallback((newMarkers) => {
    if (isRoad) {
      const paths = newMarkers.map((marker) => ({
        lat: marker.latitude,
        lng: marker.longitude,
      }));
      setPolylinePaths(paths);
    } else {
      setPolylinePaths([]);
    }
  }, [isRoad]);
  polylineSegments.push(polylinePaths);

  useEffect(() => {
    updatePolylinePaths(markers);
  }, [markers, updatePolylinePaths]);

  useEffect(() => {
    if (markers.length === 0) return;

    let tempMarkers = markers;
    if (isRoad) {
      tempMarkers = markers.map((marker) => ({ ...marker, isvisible: false }));
      let temp = { ...checkboxes };
      Object.keys(temp).forEach((key) => { temp[key]["selected"] = false; });
      setCheckboxes(temp);
      temp = { ...severityOptions };
      Object.keys(temp).forEach((key) => { temp[key]["selected"] = false; });
      setSeverityOptions(temp);
    } else {
      polylineSegments = [];
      tempMarkers = markers.map((marker) => ({ ...marker, isvisible: true }));
      let temp = { ...segmentOptions };
      Object.keys(temp).forEach((key) => { temp[key]["selected"] = false; });
      setSegmentOptions(temp);
    }
    setMarkers(tempMarkers);
  }, [isRoad]);

  // Geolocation-based centering disabled — map defaults to India center until markers load.

  // ── GIS defect-point cache helpers ────────────────────────────────────────
  // Uses sessionStorage so data is fresh on every new browser tab but instant
  // on re-navigation within the same session.
  const GIS_CACHE_TTL = 4 * 60 * 60 * 1000; // 4 hours
  const GIS_CACHE_VER = 'v1';

  const _gisHashKey = (str) => {
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
    return (h >>> 0).toString(36);
  };

  const _gisReadCache = (key) => {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const { payload, ts } = JSON.parse(raw);
      if (Date.now() - ts > GIS_CACHE_TTL) { sessionStorage.removeItem(key); return null; }
      return payload;
    } catch { return null; }
  };

  const _gisWriteCache = (key, payload) => {
    try {
      sessionStorage.setItem(key, JSON.stringify({ payload, ts: Date.now() }));
    } catch (e) {
      // sessionStorage full — skip cache write, data still shows correctly
      console.warn('GIS cache write failed (storage full?):', e.message);
    }
  };
  // ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchData = async () => {
      const userEmail = _gisGetUserEmail();
      let projectIds = Array.isArray(uid_list)
        ? uid_list.filter(Boolean).map((uid) => String(uid))
        : [];

      // No UIDs in URL — fetch all projects from get_list (cached for 15 min).
      // Scoped per user so a second login on the same tab never reads the
      // previous user's project UID list.
      if (projectIds.length === 0) {
        const listCacheKey = `gis_list_${GIS_CACHE_VER}_${userEmail}`;
        const cachedList = _gisReadCache(listCacheKey);
        if (cachedList) {
          projectIds = cachedList;
        } else {
          try {
            const listRes = await axios.get(`${process.env.REACT_APP_BASE_URL}project/get_list`);
            const inferences = listRes.data.result.inferences || [];
            projectIds = inferences.map(inf => inf.uid || inf['$oid']).filter(Boolean);
            // Short TTL for the project list so new surveys appear within 15 min
            try {
              sessionStorage.setItem(listCacheKey, JSON.stringify({ payload: projectIds, ts: Date.now() - GIS_CACHE_TTL + 15 * 60 * 1000 }));
            } catch { }
          } catch (e) {
            console.error('Failed to fetch project list:', e);
            setMarkersLoaded(true);
            return;
          }
        }
      }

      if (projectIds.length === 0) {
        // No backend projects, but Bhilwara still needs its static defect points.
        if (isBhilwaraUser) {
          const staticOnly = BHILWARA_DEFECT_POINTS.map(m => ({ ...m }));
          setMarkers(staticOnly);
          setBackupMarkerState(staticOnly);
          setWardOptions(['Bhilwara']);
          setMarkersLoaded(true);
        }
        return;
      }

      // Check in-memory cache first (instant, no size limit, survives unmount).
      // Keys scoped per user so switching logins never reads the previous user's markers.
      const sortedKey = projectIds.slice().sort().join(',');
      const markersCacheKey = `gis_markers_${GIS_CACHE_VER}_${userEmail}_${_gisHashKey(sortedKey)}`;
      const memBucket = _memCache[userEmail];

      // Bhilwara: append static defect points to whatever backend cache returned.
      // Static points are dated 2026-04-16 so the Survey Date filter surfaces them.
      const withBhilwaraStatic = (list) => isBhilwaraUser
        ? [...list, ...BHILWARA_DEFECT_POINTS.map(m => ({ ...m }))]
        : list;

      if (memBucket?.key === markersCacheKey && Date.now() - memBucket.ts < MEM_CACHE_TTL) {
        console.log(`⚡ GIS memory-cache hit: ${memBucket.data.markers.length} markers loaded instantly`);
        const merged = withBhilwaraStatic(memBucket.data.markers);
        setMarkers(merged);
        setBackupMarkerState(merged);
        setWardOptions(memBucket.data.wardOptions);
        setMarkersLoaded(true);
        return;
      }

      // Fallback: check sessionStorage cache (survives page-level re-renders)
      const cachedResult = _gisReadCache(markersCacheKey);
      if (cachedResult) {
        console.log(`✅ GIS sessionStorage cache hit: ${cachedResult.markers.length} markers loaded instantly`);
        const merged = withBhilwaraStatic(cachedResult.markers);
        setMarkers(merged);
        setBackupMarkerState(merged);
        setWardOptions(cachedResult.wardOptions);
        setMarkersLoaded(true);
        // Promote to memory cache for even faster subsequent hits
        _memCache[userEmail] = { key: markersCacheKey, data: cachedResult, ts: Date.now() };
        return;
      }

      try {
        const BATCH_SIZE = 10;
        const CONCURRENT_BATCHES = 3;
        let allInferences = [];

        const batches = [];
        for (let i = 0; i < projectIds.length; i += BATCH_SIZE) {
          batches.push(projectIds.slice(i, i + BATCH_SIZE));
        }

        console.log(`Loading ${projectIds.length} projects in ${batches.length} batches...`);

        const fetchBatch = async (batch, batchNumber) => {
          try {
            const response = await axios.post(
              `${process.env.REACT_APP_BASE_URL}project/get_project_by_id`,
              { project_uids: batch },
              { headers: { 'Content-Type': 'application/json' } }
            );
            if (response.data.result.inferences) return response.data.result.inferences;
            return [];
          } catch (batchError) {
            console.error(`Batch ${batchNumber} failed:`, batchError.message);
            return [];
          }
        };

        for (let i = 0; i < batches.length; i += CONCURRENT_BATCHES) {
          const batchGroup = batches.slice(i, i + CONCURRENT_BATCHES);
          const results = await Promise.all(batchGroup.map((batch, index) => fetchBatch(batch, i + index + 1)));
          results.forEach(inferences => { allInferences = allInferences.concat(inferences); });
          if (i + CONCURRENT_BATCHES < batches.length) {
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }

        console.log(`Total inferences loaded: ${allInferences.length}`);
        setInferenceList(allInferences);

        const normalizeLabel = (label) => label.replace(/–|−|—/g, '-').replace(/[\s_]+/g, '_');
        const uniqueCoordinatesMap = new Map();

        allInferences.forEach((inference) => {
          const { CODEBUILD_BUILD_ID, NEW_CODEBUILD_BUILD_ID } = inference.data;
          const road_rating = inference.data?.road_rating;
          const created = inference.created?.["$date"] || inference.created;
          const road_id = inference.data?.road_id || inference.road_id || '';
          const project_title_display = inference.data?.project_title_display || inference.project_title_display || '';
          const uid = inference.uid;

          (inference.data.frame_list_data || []).forEach((point) => {
            if (!point.latitude || !point.longitude || (point.latitude === 0 && point.longitude === 0)) return;
            const coordinate = `${point.latitude},${point.longitude}`;
            const existing = uniqueCoordinatesMap.get(coordinate);

            if (point.inference_info) {
              point.inference_info.forEach(info => { info.label = normalizeLabel(info.label || ''); });
            }

            if (!existing || (point.inference_info?.length > 0 && !existing.inference_info?.length)) {
              uniqueCoordinatesMap.set(coordinate, {
                ...point,
                CODEBUILD_BUILD_ID,
                NEW_CODEBUILD_BUILD_ID,
                road_rating,
                created,
                road_id,
                project_title_display,
                uid,
                isvisible: true
              });
            }
          });
        });

        const enrichedBase = Array.from(uniqueCoordinatesMap.values()).map(m => ({
          ...m,
          _ward: findNearestWard(Number(m.latitude), Number(m.longitude))
        }));

        // Bhilwara: merge static defect points (from GPX + Label Studio export)
        // alongside the backend-sourced data. The static points are dated
        // 2026-04-16, so they only render when the user picks that Survey Date.
        const enriched = isBhilwaraUser
          ? [...enrichedBase, ...BHILWARA_DEFECT_POINTS.map(m => ({ ...m }))]
          : enrichedBase;

        const wardSet = new Set(enriched.map(m => m._ward));
        const sortedWards = KHAMMAM_WARDS.filter(w => wardSet.has(w.name)).map(w => w.name);
        setWardOptions(sortedWards);
        setMarkers(enriched);
        setBackupMarkerState(enriched);
        setMarkersLoaded(true);
        console.log(`✅ ${enriched.length} markers ready, ${wardSet.size} wards`);

        // Save to in-memory cache (always works, no size limit)
        const cachePayload = { markers: enriched, wardOptions: sortedWards };
        _memCache[userEmail] = { key: markersCacheKey, data: cachePayload, ts: Date.now() };

        // Also try sessionStorage (may fail for large datasets, that's OK)
        _gisWriteCache(markersCacheKey, cachePayload);

        // Snap to roads in background. Mutate coords IN-PLACE on the shared objects
        // so every existing closure (filter, segments, etc.) immediately sees snapped
        // values — avoids the revert caused by stale-closure filter re-runs.
        snapMarkersToRoads(enriched).then(snapped => {
          if (snapped === enriched) return; // API not enabled / quota exceeded
          snapped.forEach((sm, i) => {
            enriched[i].latitude = sm.latitude;
            enriched[i].longitude = sm.longitude;
          });
          // Shallow-copy markers only (not backupMarkerState) so React re-renders
          // with snapped positions. backupMarkerState objects are the same shared
          // references — already mutated — so no need to create a new array ref
          // (avoids re-running filterCheckboxMarkers and the label-extraction effect).
          // Wrap in startTransition to keep the UI responsive during the re-render.
          startTransition(() => {
            setMarkers(prev => [...prev]);
          });
          const snappedBucket = _memCache[userEmail];
          if (snappedBucket?.key === markersCacheKey) {
            snappedBucket.data = { ...cachePayload, markers: [...enriched] };
            snappedBucket.ts = Date.now();
          }
          console.log(`📍 Road snapping applied to ${enriched.length} markers`);
        }).catch(() => { });
      } catch (err) {
        console.error("ERROR fetching markers:", err);
        setMarkersLoaded(true);
      }
    };
    fetchData();
  }, [uid_list]);

  // Fetch traffic congestion points from road data + static Bhilwara KMZ points
  useEffect(() => {
    const fetchTrafficData = async () => {
      try {
        const token = localStorage.getItem('token')?.replace(/^"|"$/g, '');
        if (!token) return;
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}dataUpload/get_excel_data`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const roads = response.data.result || [];
        const allTrafficPoints = [];
        roads.forEach(road => {
          if (road.traffic_congestion_points && road.traffic_congestion_points.length > 0) {
            road.traffic_congestion_points.forEach(tp => {
              if (tp.latitude && tp.longitude) {
                allTrafficPoints.push({
                  latitude: tp.latitude,
                  longitude: tp.longitude,
                  address: tp.address || '',
                  road_name: road.road_name || '',
                  road_id: road.road_id || ''
                });
              }
            });
          }
        });

        // Bhilwara: merge static KMZ traffic points
        if (isBhilwaraUser) {
          BHILWARA_TRAFFIC_POINTS.forEach(tp => {
            allTrafficPoints.push({
              latitude: tp.latitude,
              longitude: tp.longitude,
              address: tp.name,
              road_name: '',
              road_id: ''
            });
          });
        }

        setTrafficCongestionData(allTrafficPoints);
      } catch (err) {
        console.error('Failed to fetch traffic congestion data:', err);
      }
    };
    fetchTrafficData();
  }, [isBhilwaraUser]);

  // Extract unique defect labels from markers and build checkboxes dynamically
  useEffect(() => {
    if (backupMarkerState.length === 0) return;

    // Bhilwara: start from the fixed static label list, then append any extra
    // labels present on loaded markers that aren't already covered. Those
    // extras are the raw Label Studio export labels (Cracking, Potholes,
    // Faded Pavement Markings, …) — they need to be checkboxes so they can
    // land in the "Other" sub-category and actually filter the map.
    if (isBhilwaraUser) {
      setCheckboxes(prevCheckboxes => {
        const newCheckboxes = {
          0: { id: "0", label: "All Defect Types", prefix: "ALL", selected: prevCheckboxes[0]?.selected || false },
        };
        const enabledBhilwaraLabels = BHILWARA_STATIC_LABELS.filter(l => isLabelEnabled(l));

        const staticLower = new Set(enabledBhilwaraLabels.map(l => l.toLowerCase()));
        const extraLabels = [];
        const seenExtraLower = new Set();
        backupMarkerState.forEach(marker => {
          marker.inference_info?.forEach(info => {
            const raw = info?.label;
            if (!raw) return;
            // Treat singular "Pothole" as the canonical "Potholes" so they merge
            // into one checkbox instead of appearing as two separate filters.
            const lower = raw.toLowerCase() === 'pothole' ? 'potholes' : raw.toLowerCase();
            if (staticLower.has(lower) || seenExtraLower.has(lower)) return;
            if (!isLabelEnabled(raw)) return;
            seenExtraLower.add(lower);
            extraLabels.push(raw);
          });
        });
        extraLabels.sort();

        const allLabels = [...enabledBhilwaraLabels, ...extraLabels];
        allLabels.forEach((label, index) => {
          const existing = Object.values(prevCheckboxes).find(cb => cb.label === label);
          newCheckboxes[index + 1] = {
            id: (index + 1).toString(),
            label,
            prefix: label.split(' ').map(w => w.charAt(0)).join('').toUpperCase(),
            selected: existing?.selected || false,
          };
        });
        const currentLabels = Object.values(prevCheckboxes).map(cb => cb.label).sort().join(',');
        const newLabels = Object.values(newCheckboxes).map(cb => cb.label).sort().join(',');
        return currentLabels !== newLabels ? newCheckboxes : prevCheckboxes;
      });
      return;
    }

    // Optimized: Process labels in a single pass with memoized formatting
    const uniqueLabels = new Set();
    const labelCache = new Map(); // Cache formatted labels

    backupMarkerState.forEach(marker => {
      marker.inference_info?.forEach(info => {
        if (info.label) {
          if (!labelCache.has(info.label)) {
            labelCache.set(info.label,
              info.label
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ')
            );
          }
          uniqueLabels.add(labelCache.get(info.label));
        }
      });
    });

    // Convert Set to sorted array, filtering out disabled labels from defect config
    const sortedLabels = Array.from(uniqueLabels)
      .filter(label => isLabelEnabled(label))
      .sort();

    // Count points per defect label
    const labelCounts = {};
    backupMarkerState.forEach(marker => {
      marker.inference_info?.forEach(info => {
        if (info.label) {
          const formatted = labelCache.get(info.label);
          labelCounts[formatted] = (labelCounts[formatted] || 0) + 1;
        }
      });
    });
    console.log('=== Defect Filter Point Counts ===');
    sortedLabels.forEach(label => {
      console.log(`${label}: ${labelCounts[label] || 0} points`);
    });
    console.log('Total unique labels:', sortedLabels.length);
    console.log('==================================');

    // Build new checkboxes object
    setCheckboxes(prevCheckboxes => {
      // Build new checkboxes object, preserving existing selections
      const newCheckboxes = {
        0: { id: "0", label: "All Defect Types", prefix: "ALL", selected: prevCheckboxes[0]?.selected || false },
      };

      sortedLabels.forEach((label, index) => {
        const id = (index + 1).toString();
        // Create a prefix from the label (first letters of each word)
        const prefix = label
          .split(' ')
          .map(word => word.charAt(0))
          .join('')
          .toUpperCase();

        // Find if this label already exists in checkboxes and preserve its selection state
        const existingCheckbox = Object.values(prevCheckboxes).find(cb => cb.label === label);

        newCheckboxes[index + 1] = {
          id,
          label,
          prefix,
          selected: existingCheckbox?.selected || false
        };
      });

      // Only update if the checkboxes structure has changed (not just selection state)
      const currentLabels = Object.values(prevCheckboxes).map(cb => cb.label).sort().join(',');
      const newLabels = Object.values(newCheckboxes).map(cb => cb.label).sort().join(',');

      if (currentLabels !== newLabels) {
        return newCheckboxes;
      }

      return prevCheckboxes;
    });
  }, [backupMarkerState.length]); // only re-run when new markers arrive, not on coord-only changes (snap)

  const handleDownload = () => {
    const image = imageRef.current;
    saveAs(image.src, "image");
  };

  const selectedImageSrc = useMemo(() => selectedRadio === "original" ? imageSrc.og_src : imageSrc.ai_src, [selectedRadio, imageSrc]);


  const handleClose = () => {
    setOpen(false);
    setIsSidebarVisible(true); // Show sidebar when modal closes

    // Reverse the pan shift so the view stays in the same area
    if (mapRef.current) {
      mapRef.current.panBy(-Math.round(panelWidth / 2), 0);
    }
  };

  const handleOverviewClick = () => {
    if (!mapRef.current || markers.length === 0) return;

    const visibleMarkers = markers.filter(m => m.isvisible);
    if (visibleMarkers.length === 0) {
      alert("No visible markers to display. Please adjust your filters.");
      return;
    }

    // Close any open modals first
    if (open) {
      setOpen(false);
    }

    // Ensure sidebar is visible
    if (!isSidebarVisible) {
      setIsSidebarVisible(true);
    }

    // Create bounds to include all visible markers
    const bounds = new window.google.maps.LatLngBounds();
    visibleMarkers.forEach(marker => {
      bounds.extend(new window.google.maps.LatLng(marker.latitude, marker.longitude));
    });

    // Wait a moment for any UI updates, then fit bounds
    setTimeout(() => {
      if (!mapRef.current) return;

      // Calculate padding accounting for sidebar
      const padding = {
        top: 100,
        bottom: 100,
        left: 400,  // Extra padding for sidebar
        right: 100
      };

      // Fit the map to show all markers
      mapRef.current.fitBounds(bounds, padding);
    }, 200);
  };

  // Pan/zoom map to traffic congestion points when filter is toggled on
  useEffect(() => {
    if (!showTrafficCongestion || trafficCongestionData.length === 0 || !mapRef.current) return;
    const bounds = new window.google.maps.LatLngBounds();
    trafficCongestionData.forEach(tp => {
      bounds.extend(new window.google.maps.LatLng(tp.latitude, tp.longitude));
    });
    setTimeout(() => {
      if (!mapRef.current) return;
      mapRef.current.fitBounds(bounds, { top: 100, bottom: 100, left: 400, right: 100 });
      // Don't zoom in too much if there's only 1 point
      if (trafficCongestionData.length === 1) {
        setTimeout(() => {
          if (mapRef.current && mapRef.current.getZoom() > 16) mapRef.current.setZoom(16);
        }, 300);
      }
    }, 200);
  }, [showTrafficCongestion, trafficCongestionData]);

  const toggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  const getPathFromInference = (inference) => {
    // Show polylines in Roads mode (mapDisplayMode) OR when road segment filter is active
    if (!isRoad && mapDisplayMode !== 'road_condition') return [];

    const data = inference?.data || inference || {};
    // DEBUG — open browser console to see what's loaded
    if (process.env.NODE_ENV !== 'production') {
      console.log('[Road] road_rating:', data.road_rating ?? inference?.road_rating,
        '| frames:', (data.frame_list_data || []).length,
        '| isRoad:', isRoad, '| mode:', mapDisplayMode);
    }
    const frameList = (data.frame_list_data || [])
      .filter(entry => entry.latitude && entry.longitude && (entry.latitude !== 0 || entry.longitude !== 0))
      .map(entry => ({ lat: entry.latitude, lng: entry.longitude }));

    // Condition top-bar filter (Very Poor / Poor / Fair / Good)
    if (selectedConditions.length > 0) {
      const roadId = data.road_id || inference?.uid;
      const roadCond = roadConditionMap[roadId];
      if (roadCond && !selectedConditions.includes(roadCond)) return [];
    }

    // road_rating may be at inference.data.road_rating OR inference.road_rating, and may be a string
    const roadRating = parseFloat(data.road_rating ?? inference?.road_rating);

    // Determine which road rating bucket this inference belongs to
    let tempRoadRatingLevel = 0;
    if (!isNaN(roadRating)) {
      if (roadRating <= 40) tempRoadRatingLevel = 1;
      else if (roadRating <= 80) tempRoadRatingLevel = 2;
      else tempRoadRatingLevel = 3;
    }

    // Collect selected segment filter indices (skip index 0 = "All" header)
    const keyArray = [];
    Object.values(segmentOptions).forEach((checkbox, index) => {
      if (index > 0 && checkbox.selected) keyArray.push(index);
    });

    // No segment filter active → show all polylines
    if (keyArray.length === 0) return frameList;

    // Unknown road rating (tempRoadRatingLevel=0) → show in all modes when filter active
    if (tempRoadRatingLevel === 0) return frameList;

    // Segment filter active → only show matching road rating
    return keyArray.includes(tempRoadRatingLevel) ? frameList : [];
  };

  const handleMarkerClick = useCallback(async (
    og_file,
    ai_file,
    defectState,
    maininfo,
    latitude,
    longitude,
    codebuidlid,
    created_date,
    defect_state,
    markerId,
    markerIndex,
    gcsBucket
  ) => {
    setLoading(true);
    // setIsSidebarVisible(false); // Hide sidebar when marker is clicked
    try {
      const buildPayload = (file) => gcsBucket
        ? { gcsKey: file, gcsBucket }
        : { s3Key: file, CODEBUILD_BUILD_ID: codebuidlid };
      const [response1, response2] = await Promise.all([
        axios.post(
          `${process.env.REACT_APP_BASE_URL}project/get_signed_url`,
          buildPayload(og_file),
        ),
        axios.post(
          `${process.env.REACT_APP_BASE_URL}project/get_signed_url`,
          buildPayload(ai_file),
        ),
      ]);

      setImageSrc({ og_src: response1.data.result.url, ai_src: response2.data.result.url });
      setOpen(true);

      const uniqueLabels = [...new Set(maininfo.map(info => {
        const raw = mapLabel(info.label);
        return raw.replace(/[_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }))];
      const address = await getAddressFromGoogleMaps(latitude, longitude);
      const date = moment(created_date);

      setInfo({
        defectState: defectState,
        label: uniqueLabels,
        latitude,
        longitude,
        address,
        defect_state,
        date_time: date.tz("Asia/Kolkata").format("MMMM Do, YYYY "),
      });

      setSelectedMarker({ latitude, longitude });
      setCurrentMarkerIndex(markerIndex);

      // Pan map so the clicked point is visible in the remaining map area (left of the 700px panel)
      if (mapRef.current) {
        const map = mapRef.current;
        // First center on the marker
        map.panTo({ lat: latitude, lng: longitude });
        // Then shift the map right by half the panel width so the point sits in the center of the visible area left of the panel
        setTimeout(() => {
          map.panBy(Math.round(panelWidth / 2), 0);
        }, 100);
      }
    } catch (err) {
      console.error("Error fetching images:", err);
    }
    setLoading(false);
  }, []);

  const getAddressFromGoogleMaps = async (lat, long) => {
    let address = await Geocode.fromLatLng(lat, long);
    return address.results[0].formatted_address;
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  };

  const handlePolylineClick = async (event, inference) => {
    const { data: { road_length, frame_list_data, road_rating, data_submitted } } = inference;
    const roadRating = typeof road_rating === "number" ? road_rating : 0;
    const roadLength = typeof road_length === "number" ? road_length : 0;
    if (!frame_list_data || frame_list_data.length === 0) return;

    const entryPoint = { lat: frame_list_data[0].latitude, lng: frame_list_data[0].longitude };
    const exitPoint = { lat: frame_list_data[frame_list_data.length - 1].latitude, lng: frame_list_data[frame_list_data.length - 1].longitude };

    let totalDistance = 0;
    for (let i = 1; i < frame_list_data.length; i++) {
      const prevFrame = frame_list_data[i - 1];
      const currentFrame = frame_list_data[i];
      if (prevFrame.latitude === currentFrame.latitude && prevFrame.longitude === currentFrame.longitude) continue;
      const distance = calculateDistance(prevFrame.latitude, prevFrame.longitude, currentFrame.latitude, currentFrame.longitude);
      totalDistance += distance;
    }

    const startingAddress = await getAddressFromGoogleMaps(entryPoint.lat, entryPoint.lng);
    const endingAddress = await getAddressFromGoogleMaps(exitPoint.lat, exitPoint.lng);
    const roadCondition = roadRating <= 40 ? "Poor" : (roadRating <= 80 ? "Fair" : "Good");
    const defects = [...new Set(frame_list_data.map(item => item.defect))];
    const polylineData = {
      roadRating: roadRating,
      roadLength: roadLength,
      roadCondition: roadCondition,
      dateSubmitted: data_submitted,
      entryPoint: entryPoint,
      exitPoint: exitPoint,
      startingAddress: startingAddress,
      endingAddress: endingAddress,
      defects: defects
    };
    setSelectedPolylineData(polylineData);
    setPolylineModalOpen(true);
  };

  const getRoadColor = (roadRating) => {
    if (roadRating <= 40) return "#FF0000";
    if (roadRating > 40 && roadRating <= 80) return "#FFFF00";
    return "#00FF00";
  };

  // ─── FIX: getMarkerColor — derive color from inference_info when defect_state is "none" ───
  // REASON: Backend sends defect_state:"none" in two cases that should NOT be green:
  //   1. Frames with low-severity pavement defects (Median Separator Damaged, Missing Drain Cover Slabs)
  //   2. Frames with non-severity defects like Unauthorized Hoardings, Illegal Parking (severity:"none")
  // Without this fix all of the above fall to the default branch and render green,
  // hiding actual defects on the map.
  //
  // Color tiers when defect_state is "none" but inference_info has defects:
  //   - Has high  severity item → red
  //   - Has medium severity item → orange
  //   - Has low   severity item → yellow
  //   - Has any defect (severity "none") → light blue (#4A90D9) — observed defect, no severity
  //   - Truly empty inference_info → green (no defects at all)
  //
  // OLD LOGIC (removed — only handled explicit low/medium/high on defect_state field):
  // const getMarkerColor = useCallback((defectState, isActive) => {
  //   if (isActive) return "#00FFFF";
  //   switch ((defectState || '').toLowerCase()) {
  //     case "low": return "#C8C800";
  //     case "medium": return "#E87A00";
  //     case "high": return "#DC3C3C";
  //     default: return "#00BB00";
  //   }
  // }, []);
  const getMarkerColor = useCallback((defectState, isActive, inferenceInfo) => {
    if (isActive) return "#00FFFF";
    // Bhilwara: color derives from label+severity combination, ignoring frame-level defect_state
    if (isBhilwaraUser) return _getBhilwaraMarkerColor(inferenceInfo);
    // Color is driven by the MODE (most-frequent severity) of the inference_info
    // labels, tie-broken toward higher severity (Red > Orange > Yellow). This matches
    // the 100m segment coloring and the detail-panel badge. defect_state is only a
    // fallback when inference_info carries no usable defect color.
    // Unmapped (non-asset) labels count as low/yellow so observed defects are never hidden.
    if (inferenceInfo && inferenceInfo.length > 0) {
      const COLOR_PRIORITY = { '#DC3C3C': 3, '#E87A00': 2, '#C8C800': 1 };
      const colorCounts = {};
      inferenceInfo.forEach(info => {
        if (isAssetInventoryName(info.label)) return; // assets carry no defect color
        const color = _getLabelColor(info.label || '') || '#C8C800';
        colorCounts[color] = (colorCounts[color] || 0) + 1;
      });
      const entries = Object.entries(colorCounts);
      if (entries.length > 0) {
        return entries.sort(
          (a, b) => b[1] - a[1] || (COLOR_PRIORITY[b[0]] || 0) - (COLOR_PRIORITY[a[0]] || 0)
        )[0][0];
      }
      // No defect labels — point holds only asset-inventory items → neutral asset color.
      return ASSET_INVENTORY_COLOR;
    }
    // No inference_info — fall back to frame-level defect_state, then green.
    const state = (defectState || '').toLowerCase();
    if (state === 'high') return "#DC3C3C";  // Red
    if (state === 'medium') return "#E87A00";  // Orange
    if (state === 'low') return "#C8C800";  // Yellow
    return "#00BB00"; // Truly no defects
  }, [isBhilwaraUser]);

  // ── km per severity level ────────────────────────────────────────────────
  // Algorithm: split each road into 100m segments, classify each segment by
  // dominant frame color (getMarkerColor logic), count segments per bucket,
  // then distribute road_length proportionally → total always matches road_length.
  const kmBySeverity = useMemo(() => {
    const totals = { high: 0, medium: 0, low: 0, noDefect: 0 };

    const classifySegment = (frames) => {
      const counts = { high: 0, medium: 0, low: 0, noDefect: 0 };
      frames.forEach(frame => {
        const color = getMarkerColor(frame.defect_state, false, frame.inference_info);
        if (color === '#DC3C3C') counts.high++;
        else if (color === '#E87A00') counts.medium++;
        else if (color === '#C8C800') counts.low++;
        else counts.noDefect++;
      });
      // Dominant color wins; tie-break: high > medium > low > noDefect
      return ['high', 'medium', 'low', 'noDefect']
        .sort((a, b) => counts[b] - counts[a])[0];
    };

    // Deduplicate by road_id — same road surveyed multiple times should only count once
    const seenRoadIds = new Set();

    allInferenceList.forEach((inference) => {
      const roadId = inference.data?.road_id || inference.uid;
      if (seenRoadIds.has(roadId)) return;
      seenRoadIds.add(roadId);

      const roadLength = typeof inference.data?.road_length === 'number' ? inference.data.road_length : 0;
      if (roadLength <= 0) return;

      const frames = (inference.data?.frame_list_data || []).filter(
        f => f.latitude && f.longitude && !(f.latitude === 0 && f.longitude === 0)
      );
      if (frames.length === 0) return;

      // Count how many 100m segments fall into each bucket
      const segmentCounts = { high: 0, medium: 0, low: 0, noDefect: 0 };
      let segmentFrames = [];
      let segmentKm = 0;
      let totalSegments = 0;

      for (let i = 0; i < frames.length; i++) {
        segmentFrames.push(frames[i]);

        if (i < frames.length - 1) {
          const d = calculateDistance(
            frames[i].latitude, frames[i].longitude,
            frames[i + 1].latitude, frames[i + 1].longitude
          );
          segmentKm += d;

          if (segmentKm >= 0.1) {
            segmentCounts[classifySegment(segmentFrames)]++;
            totalSegments++;
            segmentFrames = [];
            segmentKm = 0;
          }
        }
      }

      // Last partial segment
      if (segmentFrames.length > 0) {
        segmentCounts[classifySegment(segmentFrames)]++;
        totalSegments++;
      }

      if (totalSegments === 0) return;

      // Distribute road_length proportionally → sum always equals road_length
      totals.high += (segmentCounts.high / totalSegments) * roadLength;
      totals.medium += (segmentCounts.medium / totalSegments) * roadLength;
      totals.low += (segmentCounts.low / totalSegments) * roadLength;
      totals.noDefect += (segmentCounts.noDefect / totalSegments) * roadLength;
    });

    // Save to localStorage so FinalReportDashboard can read the same values
    // even if user navigates to home before visiting GIS.
    // Key is per-user so different projects/accounts never mix.
    try {
      const userEmail = JSON.parse(window.localStorage.getItem('user'))?.email || 'default';
      localStorage.setItem(`gis_severity_km_${userEmail}`, JSON.stringify(totals));
    } catch { }

    return totals;
  }, [allInferenceList, getMarkerColor]);

  // Maps each road_id → dominant condition label for the Condition top-bar filter
  const roadConditionMap = useMemo(() => {
    const map = {};
    const seenRoadIds = new Set();
    allInferenceList.forEach((inference) => {
      const roadId = inference.data?.road_id || inference.uid;
      if (seenRoadIds.has(roadId)) return;
      seenRoadIds.add(roadId);
      const frames = (inference.data?.frame_list_data || []).filter(
        f => f.latitude && f.longitude && !(f.latitude === 0 && f.longitude === 0)
      );
      if (frames.length === 0) return;
      const counts = { high: 0, medium: 0, low: 0, noDefect: 0 };
      frames.forEach(frame => {
        const color = getMarkerColor(frame.defect_state, false, frame.inference_info);
        if (color === '#DC3C3C') counts.high++;
        else if (color === '#E87A00') counts.medium++;
        else if (color === '#C8C800') counts.low++;
        else counts.noDefect++;
      });
      const dominant = ['high', 'medium', 'low', 'noDefect'].sort((a, b) => counts[b] - counts[a])[0];
      map[roadId] = dominant === 'high' ? 'Poor'
        : dominant === 'medium' ? 'Fair'
          : 'Good'; // low or noDefect → Good
    });
    return map;
  }, [allInferenceList, getMarkerColor]);

  const getSevereState = (severity) => {
    if (severity === 0) return "None";
    if (severity === 1) return "Mild";
    if (severity >= 2 && severity <= 3) return "Moderate";
    return "Severe";
  };

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_KEYS,
    libraries,
  });

  // Set center based on URL or calculate from markers
  const { avgLat, avgLng } = useMemo(() => {
    if (markers.length === 0) return { avgLat: 0, avgLng: 0 };
    const sumLat = markers.reduce((acc, point) => acc + point.latitude, 0);
    const sumLng = markers.reduce((acc, point) => acc + point.longitude, 0);
    return {
      avgLat: sumLat / markers.length,
      avgLng: sumLng / markers.length
    };
  }, [markers]);

  useEffect(() => {
    // Check if this is the specific URL that needs custom center
    const projectIds = JSON.parse(decodeURIComponent(searchParams.get("listOfIds") || "[]"));
    const specificProjectIds = [
      "22c98a5a-92c5-4176-a714-1312b6f254a4",
      "250496f0-41fb-435a-8379-ca220a460d58",
      "46f32bb9-0e3a-450c-ad36-afdc4e1aa4a6",
      "56a7b9ce-5f72-4a49-b069-824a19fa832d",
      "5dab09c4-fe46-41bf-b271-b4665cb6223f",
      "898a2245-a6b9-4ef0-9990-c4d974ff5f60",
      "a4a2ee84-1ba6-44b7-a4ca-45303c3772d8",
      "e244ea60-e890-4e2e-b642-dfa0130c729d",
      "ebbd772f-7f55-4cec-a88c-bc84f46ecdba"
    ];

    // Check if all specific project IDs are present in the URL
    const hasAllSpecificIds = specificProjectIds.every(id => projectIds.includes(id)) &&
      projectIds.length === specificProjectIds.length;

    if (hasAllSpecificIds) {
      // Use specific coordinates for this URL
      setCenter({ lat: 25.368259, lng: 74.665789 });
    } else if (avgLat !== 0 || avgLng !== 0) {
      // Use average of markers for other URLs (skip when markers not yet loaded)
      // setCenter({ lat: avgLat, lng: avgLng });
    }
  }, [avgLat, avgLng, searchParams]);

  useEffect(() => {
    if (selectedMarker.latitude !== null && selectedMarker.longitude !== null) {
      setCenter({ lat: selectedMarker.latitude, lng: selectedMarker.longitude });
    }
  }, [selectedMarker]);

  const mapId = "c7ce1a914d921fb";
  const defaultCustomMarker = {
    path: "M 0,0 m -5,0 a 5,5 0 1,0 10,0 a 5,5 0 1,0 -10,0", // Circle path (dot)
    fillColor: "#FF0000",
    fillOpacity: 1,
    strokeColor: "#000000",
    strokeWeight: 1.5,
    scale: 1,
  };
  const selectedCustomMarker = {
    ...defaultCustomMarker,
    fillColor: "#00FFFF",
    strokeColor: "#000000",
    strokeWeight: 2.5,
    scale: 1.4
  };

  const calculateScale = (zoom) => {
    // Small scale for dot-like markers
    if (zoom <= 14) return 0.6;
    if (zoom <= 16) return 0.7;
    if (zoom <= 18) return 0.8;
    return 0.9;
  };

  const handleZoomChanged = useCallback(function () {
    const newZoomLevel = this.getZoom();

    // Throttle zoom updates to improve performance
    if (zoomTimeoutRef.current) {
      clearTimeout(zoomTimeoutRef.current);
    }

    zoomTimeoutRef.current = setTimeout(() => {
      setZoomLevel(newZoomLevel);
    }, 100);
  }, []);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;

    // Initialize deck.gl overlay — but only if the browser can give us a WebGL2
    // context. Without it, luma.gl throws during device init and crashes the page,
    // so we fall back to the plain Google Map (markers/polylines via the JS API
    // still render; only the deck.gl defect/heatmap layers are unavailable).
    if (isWebGL2Available()) {
      const overlay = new GoogleMapsOverlay({
        layers: [],
        getCursor: () => 'pointer'
      });
      overlay.setMap(map);
      overlayRef.current = overlay;
    } else {
      overlayRef.current = null;
      setWebglUnavailable(true);
      console.warn('WebGL2 unavailable — deck.gl layers disabled. Enable hardware acceleration in your browser.');
    }

    setIsMapReady(true);

    // Check if this is the specific URL that needs custom center
    const projectIds = JSON.parse(decodeURIComponent(searchParams.get("listOfIds") || "[]"));
    const specificProjectIds = [
      "22c98a5a-92c5-4176-a714-1312b6f254a4",
      "250496f0-41fb-435a-8379-ca220a460d58",
      "46f32bb9-0e3a-450c-ad36-afdc4e1aa4a6",
      "56a7b9ce-5f72-4a49-b069-824a19fa832d",
      "5dab09c4-fe46-41bf-b271-b4665cb6223f",
      "898a2245-a6b9-4ef0-9990-c4d974ff5f60",
      "a4a2ee84-1ba6-44b7-a4ca-45303c3772d8",
      "e244ea60-e890-4e2e-b642-dfa0130c729d",
      "ebbd772f-7f55-4cec-a88c-bc84f46ecdba"
    ];

    const hasAllSpecificIds = specificProjectIds.every(id => projectIds.includes(id)) &&
      projectIds.length === specificProjectIds.length;

    if (hasAllSpecificIds && markers.length > 0) {
      // Use specific coordinates and zoom for this URL
      map.setCenter({ lat: 25.365649, lng: 74.658777 });
      map.setZoom(17);
    } else if (chainageBounds) {
      // Chainage-segment focus: fit tightly to the requested 100m bucket so the
      // map opens zoomed to that segment rather than the whole road.
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: chainageBounds.minLat, lng: chainageBounds.minLng });
      bounds.extend({ lat: chainageBounds.maxLat, lng: chainageBounds.maxLng });
      map.fitBounds(bounds, { top: 80, bottom: 100, left: 50, right: 50 });
    } else if (markers.length > 0) {
      // Fit bounds to show all markers for other URLs
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(marker => {
        bounds.extend({ lat: marker.latitude, lng: marker.longitude });
      });
      map.fitBounds(bounds);

      // Set default zoom level after fitting bounds (for initial load only)
      window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        const currentZoom = map.getZoom();
        /*
        if (currentZoom && currentZoom < 17) {
          map.setZoom(17);
        }
        */
        // Ensure map does not zoom out too far or zoom in too much beyond what's needed for Khammam
      });
    }
  }, [markers, searchParams, chainageBounds]);

  const handleMapIdle = useCallback(() => {
    if (!isMapReady) {
      setIsMapReady(true);
    }
  }, [isMapReady]);

  // pageLoading kept for backwards compatibility but no longer blocks rendering
  useEffect(() => {
    if (isLoaded && isMapReady && markersLoaded) {
      setPageLoading(false);
    }
  }, [isLoaded, isMapReady, markersLoaded]);

  // Auto-fit map to markers as soon as the first batch arrives
  useEffect(() => {
    if (hasInitialFit) return;
    if (!mapRef.current || markers.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach(m => {
      bounds.extend({ lat: m.latitude, lng: m.longitude });
    });

    const isKhammam = (JSON.parse(window.localStorage.getItem("user"))?.email || "").toLowerCase() === "khammam@roadvision.ai";

    if (isKhammam) {
      // User requested "Default as Khammam" and "more zoom overview".
      // We skip fitBounds so it doesn't zoom out to show outlier points.
      mapRef.current.setCenter({ lat: 17.25, lng: 80.15 });
      mapRef.current.setZoom(13.7);
    } else {
      mapRef.current.fitBounds(bounds);

      // Optional: After fitting bounds, restore to a reasonable zoom if it zoomed in too much
      setTimeout(() => {
        if (mapRef.current && mapRef.current.getZoom() > 14) {
          mapRef.current.setZoom(14);
        }
      }, 100);
    }

    setHasInitialFit(true);
  }, [markers, hasInitialFit]);

  // segmentOptions, checkboxes, severityOptions, roadsegmentOptions (kept the same)
  const [segmentOptions, setSegmentOptions] = useState({
    0: { label: "All", selected: false },
    1: { label: "Poor", color: "#FF0000", selected: false },
    2: { label: "Fair", color: "#FFA500", selected: false },
    3: { label: "Good", color: "#00FF00", selected: false },
  });

  const handleSegmentOptionState = (id) => {
    let temp = { ...segmentOptions };
    if (id === 0) {
      const allSelected = !temp[0].selected;
      Object.keys(temp).forEach((key) => {
        temp[key].selected = allSelected;
      });
    } else {
      temp[id].selected = !temp[id].selected;
      if (Object.values(temp).slice(1).every((option) => option.selected)) {
        temp[0].selected = true;
      } else {
        temp[0].selected = false;
      }
    }
    setSegmentOptions(temp);
  };

  useEffect(() => {
    let keyArray = Object.values(segmentOptions).slice(1).filter((checkbox) => checkbox.selected).map((checkbox) => checkbox.label.toLowerCase());
    if (keyArray.length !== 0) {
      setIsRoad(true);
      setAllFalse("checkboxes");
      setAllFalse("severity");
    } else {
      setIsRoad(false);
    }
  }, [segmentOptions]);

  const [defectSearchTerm, setDefectSearchTerm] = useState("");
  const [openSections, setOpenSections] = useState({ pavement: true, assets: false, roadSafety: false });
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [showDefectFilters, setShowDefectFilters] = useState(true);
  const [showLegend, setShowLegend] = useState(false);
  const [wardDropdownOpen, setWardDropdownOpen] = useState(false);
  const [defectDropdownOpen, setDefectDropdownOpen] = useState(false);
  const wardDropdownRef = useRef(null);
  const defectDropdownRef = useRef(null);

  // ─── Top Filter Bar State (display-only) ───
  const ROAD_NAME_OPTIONS = ['Ward 1', 'Ward 2', 'Ward 5', 'Minor_Road'];
  const AUTHORITY_OPTIONS = AUTHORITY_OPTIONS_FROM_CTX.length > 0 ? AUTHORITY_OPTIONS_FROM_CTX : ['PWD', 'UIT', 'NHAI'];
  const ROAD_TYPE_OPTIONS = ['National Highway', 'State Highway', 'City Main Road', 'Ward Road', 'Internal Road'];
  const CONDITION_OPTIONS = ['Good', 'Fair', 'Poor'];

  const [selectedRoadNames, setSelectedRoadNames] = useState([]);
  const [selectedAuthorities, setSelectedAuthorities] = useState([]);
  const [selectedRoadTypes, setSelectedRoadTypes] = useState([]);
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [selectedSurveyDate, setSelectedSurveyDate] = useState('');

  const [roadNameDropdownOpen, setRoadNameDropdownOpen] = useState(false);
  const [authorityDropdownOpen, setAuthorityDropdownOpen] = useState(false);
  const [roadTypeDropdownOpen, setRoadTypeDropdownOpen] = useState(false);
  const [conditionDropdownOpen, setConditionDropdownOpen] = useState(false);
  const [surveyDateOpen, setSurveyDateOpen] = useState(false);

  const roadNameDropdownRef = useRef(null);
  const authorityDropdownRef = useRef(null);
  const roadTypeDropdownRef = useRef(null);
  const conditionDropdownRef = useRef(null);
  const surveyDateRef = useRef(null);

  const toggleTopFilter = (setter, value) =>
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);

  const clearTopFilters = () => {
    setSelectedRoadNames([]);
    setSelectedAuthorities([]);
    setSelectedRoadTypes([]);
    setSelectedConditions([]);
    setSelectedSurveyDate('');
    setShowTrafficCongestion(false);
  };

  const hasTopFilters = selectedRoadNames.length > 0 || selectedAuthorities.length > 0 ||
    selectedRoadTypes.length > 0 || selectedConditions.length > 0 || selectedSurveyDate !== '' || showTrafficCongestion;

  // Derive unique survey dates from loaded markers (YYYY-MM-DD, newest first)
  const uniqueSurveyDates = useMemo(() => {
    const toDateStr = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toISOString().slice(0, 10);
    };
    return [...new Set(backupMarkerState.map(m => toDateStr(m.created)).filter(Boolean))]
      .sort((a, b) => new Date(b) - new Date(a));
  }, [backupMarkerState]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wardDropdownRef.current && !wardDropdownRef.current.contains(e.target)) {
        setWardDropdownOpen(false);
      }
      if (defectDropdownRef.current && !defectDropdownRef.current.contains(e.target)) {
        setDefectDropdownOpen(false);
      }
      if (roadNameDropdownRef.current && !roadNameDropdownRef.current.contains(e.target)) {
        setRoadNameDropdownOpen(false);
      }
      if (authorityDropdownRef.current && !authorityDropdownRef.current.contains(e.target)) {
        setAuthorityDropdownOpen(false);
      }
      if (roadTypeDropdownRef.current && !roadTypeDropdownRef.current.contains(e.target)) {
        setRoadTypeDropdownOpen(false);
      }
      if (conditionDropdownRef.current && !conditionDropdownRef.current.contains(e.target)) {
        setConditionDropdownOpen(false);
      }
      if (surveyDateRef.current && !surveyDateRef.current.contains(e.target)) {
        setSurveyDateOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Ward Filter State ───
  const [wardSearchTerm, setWardSearchTerm] = useState("");
  const [selectedWardFilters, setSelectedWardFilters] = useState([]);
  const [wardOptions, setWardOptions] = useState([]);        // unique sorted ward names (only those with points)

  // Filter ward options based on search
  const filteredWardOptions = useMemo(() => {
    if (!wardSearchTerm.trim()) return wardOptions;
    const s = wardSearchTerm.toLowerCase();
    return wardOptions.filter(w => w.toLowerCase().includes(s));
  }, [wardOptions, wardSearchTerm]);

  // Handle ward filter toggle
  const handleWardFilterToggle = useCallback((ward) => {
    setSelectedWardFilters(prev => {
      if (ward === '__ALL__') {
        return prev.length === wardOptions.length ? [] : [...wardOptions];
      }
      return prev.includes(ward) ? prev.filter(w => w !== ward) : [...prev, ward];
    });
  }, [wardOptions]);

  const [checkboxes, setCheckboxes] = useState({
    0: { id: "0", label: "All Defect Types", prefix: "ALL", selected: false },
  });

  // Filter defect types based on search term
  const filteredCheckboxes = useMemo(() => {
    if (!defectSearchTerm.trim()) return checkboxes;

    const searchLower = defectSearchTerm.toLowerCase();
    const filtered = {};

    Object.entries(checkboxes).forEach(([key, value]) => {
      if (value.label.toLowerCase().includes(searchLower)) {
        filtered[key] = value;
      }
    });

    return filtered;
  }, [checkboxes, defectSearchTerm]);

  // Categorize checkboxes into Pavement / Assets / Road Safety groups
  // Subcategory configuration for defect filter dropdown
  const SUB_CATEGORIES_CONFIG = useMemo(() => {
    // Asset Inventory (4th group) is appended to whichever defect config applies.
    if (isBhilwaraUser) return { ...BHILWARA_SUB_CATEGORIES_CONFIG, ...ASSET_INVENTORY_CONFIG };
    return ({
    ...ASSET_INVENTORY_CONFIG,
    pavement: {
      pavement_condition: {
        label: 'Pavement Condition',
        names: ['potholes', 'cracking', 'rutting', 'stripping/delamination',
          'pavement damage (severe)', 'unsealed road', 'settlement',
          'water stagnation', 'pavement joint',
          'water_stagnation'],
      },
      shoulder_condition: {
        label: 'Shoulder Condition',
        names: ['shoulder - edge drop', 'shoulder - unevenness', 'shoulder - rain cuts',
          'unevenness_/_edge_drop_of_shoulder', 'rain_cuts'],
      },
    },
    assets: {
      signages: {
        label: 'Signages',
        names: ['damaged sign boards / sign structures', 'signage - poor visibility (day)', 'signage - poor visibility (night)',
          'damaged_sign_boards_/_sign_structures', 'visibility_of_signages'],
      },
      highway_lighting: {
        label: 'Highway Lighting',
        names: ['damaged highway lights', 'non-functional highway lights',
          'highway_lights_damaged'],
      },
      change_detection: {
        label: 'Change Detection',
        names: ['missing assets (street lights)'],
      },
      crash_barriers: {
        label: 'Crash Barriers & Guard Rails',
        names: ['damaged crash barriers', 'damaged (mbcb) metal beam crash barrier', 'damaged (pgr) pedestrian guard rail',
          'damaged_crash_barriers_/_guardrails'],
      },
      kerb_median: {
        label: 'Kerb & Median',
        names: ['damaged kerb',
          'damaged_kerb_/_faded_kerb_painting'],
      },
      drainage: {
        label: 'Drainage',
        names: ['damaged drain cover slabs', 'missing drain cover slabs',
          'damaged_/_missing_drain_cover_slabs'],
      },
      footpath: {
        label: 'Footpath',
        names: ['damaged footpath tiles / paver blocks',
          'damaged_footpath'],
      },
      safety_devices: {
        label: 'Safety Devices',
        names: ['damaged blinkers', 'damaged attenuators', 'damaged delineators', 'damaged anti-glare'],
      },
      road_studs_markers: {
        label: 'Road Studs & Markers',
        names: ['damaged hazard markers', 'damaged road studs', 'road studs - poor visibility (day)', 'road studs - poor visibility (night)',
          'missing/damaged_road_studs_/_rumble_strips_/_hazard_markers', 'visibility_of_studs_&_markers'],
      },
      bus_bay: {
        label: 'Bus Bay & Truck Lay-By',
        names: ['bus bay - damaged shelters', 'bus bay - faded markings', 'bus bay - damaged signages',
          'truck lay by - damaged shelters', 'truck lay by - faded markings', 'truck lay by - damaged signages'],
      },
      plantation: {
        label: 'Plantation / Landscaping',
        names: ['missing plants / irregular gaps (median)', 'deteriorated or damaged plants (median)', 'reduced visibility due to overgrowth',
          'damaged_/_deteriorated_plants', 'missing_plants_/_irregular_gaps', 'reduced_sight_distance'],
      },
      unauthorized: {
        label: 'Unauthorized Activity',
        names: ['unauthorized hoardings',
          'unauthorized_signboards_/_hoardings', 'holding_/_advertisement_boards'],
      },
    },
    roadSafety: {
      rumble_strips: {
        label: 'Rumble Strips',
        names: ['damaged rumble strips'],
      },
      pavement_markings: {
        label: 'Pavement Markings',
        names: ['faded pavement marking', 'pavement marking - poor visibility (day)', 'pavement marking - poor visibility (night)',
          'pavement_marking', 'pavement_marking_visibility'],
      },
      work_zone: {
        label: 'Work Zone Safety',
        names: ['work zone - inadequate signboard visibility', 'work zone - inadequate barricading', 'work zone - poor diversion arrangement / condition',
          'work_zone_safety', 'inappropriate_diversions'],
      },
      crash_barriers: {
        label: 'Crash Barriers & Guard Rails',
        names: ['faded painting concrete crash (cc) barrier', 'missing assets (guard rails)', 'barriers - faded painting guard rails',
          'faded_painting_on_barriers'],
      },
      kerb_median: {
        label: 'Kerb & Median',
        names: ['faded kerb painting', 'median separator damaged', 'median separator paint faded'],
      },
      drainage: {
        label: 'Drainage',
        names: ['manhole cover'],
      },
      unauthorized: {
        label: 'Unauthorized Activity',
        names: ['unauthorized median openings', 'general encroachments', 'illegal parking', 'unauthorized signboards',
          'unauthorized_median_openings', 'encroachment', 'encroachments/_illegal_parking'],
      },
      vegetation: {
        label: 'Vegetation & Cleanliness',
        names: ['shoulder - vegetation growth', 'excessive plantation growth', 'reduced visibility due to plantation growth',
          'cleanliness - litter', 'cleanliness - debris',
          'cleanliness', 'vegetation_growth_on_shoulders'],
      },
    },
  }); }, [isBhilwaraUser]);

  const getSeverityOrder = useCallback((label) => {
    if (!label) return 2;
    const n = label.toLowerCase();
    // High (0)
    if (n.includes('pavement damage') || (n.includes('pavement') && n.includes('severe'))) return 0;
    if (n === 'potholes' || n === 'pothole') return 0;
    if (n.includes('settlement')) return 0;
    if (n.includes('rutting')) return 0;
    if (n.includes('edge drop')) return 0;
    if (n.includes('damaged sign board') || n.includes('damaged sign structure')) return 0;
    if (n.includes('damaged highway light')) return 0;
    if (n.includes('non-functional') || n.includes('non functional')) return 0;
    if (n.includes('missing assets') && n.includes('street')) return 0;
    if (n.includes('damaged crash barrier')) return 0;
    if (n.includes('metal beam crash barrier') || n.includes('mbcb')) return 0;
    if (n.includes('pedestrian guard rail')) return 0;
    if (n.includes('damaged kerb') && !n.includes('faded') && !n.includes('painting')) return 0;
    if (n.includes('damaged drain cover') || n.includes('missing drain cover')) return 0;
    if (n.includes('damaged blinker')) return 0;
    if (n.includes('damaged attenuator')) return 0;
    if (n.includes('damaged delineator')) return 0;
    if (n.includes('anti-glare') || n.includes('anti glare')) return 0;
    if (n.includes('damaged hazard marker')) return 0;
    if (n.includes('damaged rumble strip')) return 0;
    if (n.includes('faded pavement marking')) return 0;
    if (n.includes('pavement marking') && n.includes('night')) return 0;
    if (n.includes('work zone') && n.includes('barricading')) return 0;
    if (n.includes('work zone') && n.includes('diversion')) return 0;
    if (n.includes('missing assets') && n.includes('guard rail')) return 0;
    if (n.includes('median separator') && !n.includes('paint')) return 0;
    if (n.includes('median opening') || n.includes('unauthorized median')) return 0;
    if (n.includes('reduced visibility')) return 0;
    // Medium (1)
    if (n.includes('stripping') || n.includes('delamination')) return 1;
    if (n.includes('cracking')) return 1;
    if (n.includes('water stagnation')) return 1;
    if (n.includes('unevenness')) return 1;
    if (n.includes('rain cut')) return 1;
    if (n.includes('signage') && n.includes('poor visibility')) return 1;
    if (n.includes('damaged road stud')) return 1;
    if (n.includes('bus bay') && n.includes('shelter')) return 1;
    if (n.includes('truck lay by') && n.includes('shelter')) return 1;
    if (n.includes('missing plants') || n.includes('irregular gap')) return 1;
    if (n.includes('deteriorated')) return 1;
    if (n.includes('hoarding')) return 1;
    if (n.includes('pavement marking') && n.includes('poor visibility') && n.includes('day')) return 1;
    if (n.includes('work zone') && n.includes('signboard')) return 1;
    if (n.includes('faded painting')) return 1;
    if (n.includes('faded kerb') || (n.includes('kerb') && n.includes('faded'))) return 1;
    if (n.includes('median separator') && n.includes('paint')) return 1;
    if (n.includes('general encroachment')) return 1;
    if (n.includes('damaged footpath') || n.includes('paver block')) return 1;
    // Low (2)
    return 2;
  }, []);

  const categorizedCheckboxes = useMemo(() => {
    const searchLower = defectSearchTerm.toLowerCase().trim();

    // Build a lookup: lowercase label -> { key, ...cb }
    const cbByLabel = {};
    Object.entries(checkboxes).forEach(([key, cb]) => {
      if (key === '0') return;
      if (searchLower && !cb.label.toLowerCase().includes(searchLower)) return;
      cbByLabel[cb.label.toLowerCase()] = { key, ...cb };
    });

    const result = { pavement: [], assets: [], roadSafety: [], assetInventory: [] };
    // Bhilwara allows the same label to appear across multiple top-level tabs,
    // so we can't use deletion from cbByLabel as the "matched" signal — it would
    // strip the label after the first tab. Track matches in a side set instead,
    // and let "Other" contain only labels that never matched any sub-category
    // (i.e. the new Bhilwara export labels with no category mapping yet).
    const matchedNames = new Set();

    // For each main category, group by subcategory
    Object.entries(SUB_CATEGORIES_CONFIG).forEach(([catKey, subCats]) => {
      const subGroups = [];
      Object.entries(subCats).forEach(([subKey, { label: subLabel, names }]) => {
        const items = [];
        names.forEach(name => {
          const cb = cbByLabel[name];
          if (cb) {
            items.push(cb);
            matchedNames.add(name);
            if (!isBhilwaraUser) delete cbByLabel[name]; // non-Bhilwara: label in one tab only
          }
        });
        // Sort items within subcategory by severity
        items.sort((a, b) => getSeverityOrder(a.label) - getSeverityOrder(b.label));
        if (items.length > 0) {
          subGroups.push({ subKey, subLabel, items });
        }
      });
      result[catKey] = subGroups;
    });

    // "Other" only contains labels that didn't match any sub-category — labels
    // already shown under Pavement Condition / Asset Management / etc. are
    // never duplicated here.
    const otherItems = Object.values(cbByLabel).filter(cb =>
      !matchedNames.has(cb.label.toLowerCase())
    );
    if (otherItems.length > 0) {
      otherItems.sort((a, b) => getSeverityOrder(a.label) - getSeverityOrder(b.label));
      result.roadSafety.push({ subKey: 'other', subLabel: 'Other', items: otherItems });
    }

    return result;
  }, [checkboxes, defectSearchTerm, SUB_CATEGORIES_CONFIG, getSeverityOrder]);

  // Pre-select defect types from URL parameter (used when navigating from Dashboard)
  useEffect(() => {
    if (defectTypesAppliedRef.current) return; // already applied, don't re-run
    const defectTypesParam = searchParams.get("defectTypes");
    if (!defectTypesParam) return;
    // Only apply once checkboxes are populated (more than just "All Defect Types")
    if (Object.keys(checkboxes).length <= 1) return;

    try {
      const requestedDefects = JSON.parse(decodeURIComponent(defectTypesParam));
      if (!Array.isArray(requestedDefects) || requestedDefects.length === 0) return;

      defectTypesAppliedRef.current = true; // mark as applied

      const STOP_WORDS = new Set(['of', 'on', 'in', 'the', 'a', 'an', 'and', 'or', '-', '/']);

      // Tokenize: lowercase, replace punctuation (incl. parens, slashes,
      // hyphens) with spaces, drop trailing 's' on each word, drop stop words.
      // This makes "Pavement Marking (Faded)" and "Faded Pavement Marking"
      // both reduce to {pavement, marking, faded}, so reordered or
      // parenthesized labels still match. Severity-suffix labels like "(High)"
      // are also flattened, but severity is filtered separately so this is OK.
      const tokenize = (s) =>
        new Set(
          s.toLowerCase()
            .replace(/\b(?:high|medium|low)\b/g, ' ')
            .replace(/[^a-z0-9]+/g, ' ')
            .split(/\s+/)
            .filter(Boolean)
            .map(w => w.replace(/s$/, ''))
            .filter(w => w && !STOP_WORDS.has(w))
        );

      const requestedTokenSets = requestedDefects.map(d => tokenize(d));

      setCheckboxes(prev => {
        const updated = { ...prev };
        let anyMatched = false;
        Object.keys(updated).forEach(key => {
          if (key === '0') return; // skip "All Defect Types"
          const labelTokens = tokenize(updated[key].label);
          if (labelTokens.size === 0) return;
          if (requestedTokenSets.some(rTokens => {
            if (rTokens.size === 0) return false;
            // Match when one token-set is a subset of the other (handles
            // "Vegetation Growth On Shoulders" ⊇ "Shoulder Vegetation Growth"
            // after stop-word/plural stripping).
            const [small, big] = rTokens.size <= labelTokens.size
              ? [rTokens, labelTokens]
              : [labelTokens, rTokens];
            for (const t of small) if (!big.has(t)) return false;
            return true;
          })) {
            updated[key] = { ...updated[key], selected: true };
            anyMatched = true;
          }
        });
        return anyMatched ? updated : prev;
      });

      // Clear the defectTypes param from URL after applying
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("defectTypes");
      setSearchParams(newParams, { replace: true });
    } catch (e) {
      console.error("Error parsing defectTypes param:", e);
    }
  }, [checkboxes, searchParams, setSearchParams]);

  const [severityOptions, setSeverityOptions] = useState({
    0: { label: "Select All", color: "#1967D2", selected: false },
    1: { label: "Low", color: "#E1E105", checkedColor: "#E1E105", selected: false },
    2: { label: "Medium", color: "#FFA500", checkedColor: "#FFA500", selected: false },
    3: { label: "High", color: "#9E0042", checkedColor: "#FF0000", selected: false },
    4: { label: "No Defect", color: "#00BB00", checkedColor: "#00BB00", selected: false },
  });

  const [roadsegmentOptions, setRoadSegmentOptions] = useState({
    0: { id: "0", label: "All Feature Types", prefix: "ALL", selected: false },
    1: { id: "1", label: "Crash Barriers Guard Rails Damaged", prefix: "CBGRD", selected: false },
    2: { id: "2", label: "Damaged Blinkers Attenuators Delineators Anti Glare", prefix: "DBADA", selected: false },
    3: { id: "3", label: "Damaged Bus Shelters Faded Markings Damaged Signages", prefix: "DBSFMDS", selected: false },
    4: { id: "4", label: "Damaged Lights", prefix: "DL", selected: false },
    5: { id: "5", label: "Damaged Road Studs Rumble Strips Hazard Markers", prefix: "DRSRSHM", selected: false },
    6: { id: "6", label: "Damaged Sign Boards Structures", prefix: "DSBS", selected: false },
    7: { id: "7", label: "Encroachments Illegal Parking", prefix: "EIP", selected: false },
    8: { id: "8", label: "Faded Painting Barrier Guard Rail", prefix: "FPBGR", selected: false },
    9: { id: "9", label: "Faded Pavement Marking", prefix: "FPM", selected: false },
    10: { id: "10", label: "Inappropriate Diversions", prefix: "ID", selected: false },
    11: { id: "11", label: "Missing Assets Change Detection", prefix: "MACD", selected: false },
    12: { id: "12", label: "Missing Poor Safety Measures", prefix: "MPSM", selected: false },
    13: { id: "13", label: "Non Functional Lights", prefix: "NFL", selected: false },
    14: { id: "14", label: "Overall Cleanliness", prefix: "OC", selected: false },
    15: { id: "15", label: "Pavement Marking Visibility Day Night", prefix: "PMVDN", selected: false },
    16: { id: "16", label: "Road Stud Rumble Strip Visibility Day Night", prefix: "RSRSVDN", selected: false },
    17: { id: "17", label: "Signage Visibility Day Night", prefix: "SVDN", selected: false },
    18: { id: "18", label: "Unauthorized Median Openings", prefix: "UMO", selected: false },
    19: { id: "19", label: "Unauthorized Signboards Hoardings", prefix: "USH", selected: false },
  });

  const handleCheckboxChange = (event, id) => {
    setIsRoad(false);
    if (id === 0 && event.target.checked) setAllTrue("checkboxes");
    else if (id === 0 && !event.target.checked) setAllFalse("checkboxes");
    if (id !== 0) {
      const temp = { ...checkboxes };
      temp[id]["selected"] = !temp[id]["selected"];
      setCheckboxes(temp);
    }
  };

  const setAllTrue = (setKey) => {
    let temp = {};
    if (setKey === "checkboxes") {
      temp = { ...checkboxes };
      Object.keys(temp).forEach((key) => { temp[key]["selected"] = true; });
      setCheckboxes(temp);
    }
    if (setKey === "severity") {
      temp = { ...severityOptions };
      Object.keys(temp).forEach((key) => { temp[key]["selected"] = true; });
      setSeverityOptions(temp);
    }
  };

  const setAllFalse = (setKey) => {
    let temp = {};
    if (setKey === "checkboxes") {
      temp = { ...checkboxes };
      Object.keys(temp).forEach((key) => { temp[key]["selected"] = false; });
      setCheckboxes(temp);
    }
    if (setKey === "severity") {
      temp = { ...severityOptions };
      Object.keys(temp).forEach((key) => { temp[key]["selected"] = false; });
      setSeverityOptions(temp);
    }
  };

  const checkIfAllPresent = (number, array) => {
    return JSON.stringify(Array.from({ length: number - 1 }, (_, i) => i + 1)) === JSON.stringify(array.sort());
  };

  // Normalize dashes in labels (convert en-dashes to regular hyphens)
  const normalizeLabel = (label) => label.replace(/–|−|—/g, '-');

  const createLabelMappingForCheckboxes = () => {
    const mapping = {};

    // Map each checkbox label to backend label patterns
    const labelToBackendMapping = {
      'potholes': ['pothole', 'potholes'],
      'pothole (high)': ['pothole', 'potholes'],
      'pothole (medium)': ['pothole', 'potholes'],
      'pothole (low)': ['pothole', 'potholes'],
      'cracking (high)': ['crack', 'cracking'],
      'cracking (medium)': ['crack', 'cracking'],
      'cracking (low)': ['crack', 'cracking'],
      'rutting (high)': ['rut', 'rutting'],
      'rutting (medium)': ['rut', 'rutting'],
      'rutting (low)': ['rut', 'rutting'],
      'rain cuts': ['rain'],
      'water stagnation': ['water', 'stagnation'],
      'pavement marking (faded)': ['Pavement Marking'],
      'pavement marking visibility (day/night)': ['Pavement Marking Visibility'],
      'missing/damaged road studs / rumble strips / hazard markers': ['Missing/damaged Road Studs / Rumble Strips / Hazard Markers'],
      'visibility of studs & markers (day/night)': ['road_stud_rumble_strip_visibility_day_night'],
      'unevenness / edge drop of shoulder': ['edge_drop', 'uneven'],
      'vegetation growth on shoulders': ['vegetation'],
      'damaged / missing drain cover slabs': ['damaged_missing_cover_slabs', 'cover_slab'],
      'damaged footpath (tiles/pavers)': ['damaged_footpath', 'footpath'],
      'cleanliness (poor)': ['overall_cleanliness', 'cleanliness'],
      'Encroachments/ illegal parking': ['Encroachments_illegal_parking'],
      'missing plants / irregular gaps': ['missing_plants_irregular_gaps', 'missing'],
      'damaged / deteriorated plants': ['deteriorated_damaged_plants', 'deteriorated'],
      'unauthorized median openings': ['unauthorized_median_openings'],
      'reduced sight distance (due to overgrowth)': ['reduced_visibility_due_to_plant_growth', 'reduced'],
      'damaged kerb / faded kerb painting': ['damaged_kerb_faded_paint', 'kerb'],
      'damaged crash barriers / guardrails (cc/mbcb)': ['crash_barriers_guard_rails_damaged', 'crash_barrier'],
      'faded painting on barriers': ['faded_painting_barrier_guard_rail'],
      'damaged sign boards / sign structures': ['damaged_sign_boards_structures', 'sign_board'],
      'visibility of signages (day/night)': ['signage_visibility_day_night'],
      'damaged blinkers / attenuators / delineators/ anti-glare': ['damaged_blinkers_attenuators_delineators_anti_glare'],
      'highway lights damaged': ['damaged_lights', 'lights'],
      'non-functional lights': ['non_functional_lights'],
      'unauthorized signboards / hoardings': ['unauthorized_signboards_hoardings'],
      'inappropriate diversions': ['inappropriate_diversions'],
      'damaged bus shelters': ['damaged_bus_shelters_faded_markings_damaged_signages', 'bus_shelter'],
      'faded bus bay / truck lay-by markings': ['faded_bus_bay', 'bus_bay'],
      'holding / advertisement boards': ['advertisement', 'hoarding'],
      'work zone safety (signboard visibility, barricading)': ['work_zone', 'safety'],
      'patching': ['patching', 'patch'],
      'Encroachment': ['Encroachment']
    };

    Object.keys(checkboxes).forEach(key => {
      const index = parseInt(key);
      if (index > 0) {
        const rawLabel = checkboxes[key].label.toLowerCase();
        const label = normalizeLabel(rawLabel); // Normalize dashes
        // Check if there's a mapping for this label
        let backendLabels = null;
        for (const [mappingKey, mappingValue] of Object.entries(labelToBackendMapping)) {
          if (normalizeLabel(mappingKey) === label) {
            backendLabels = mappingValue;
            break;
          }
        }
        // If no mapping found, just use the normalized label
        // (markers are already normalized to this form)
        if (!backendLabels) {
          backendLabels = [label];
        }
        // Normalize all backend labels and remove duplicates
        const normalizedBackendLabels = backendLabels.map(bl => normalizeLabel(bl.toLowerCase()));
        mapping[label] = [...new Set(normalizedBackendLabels)];
      }
    });

    return mapping;
  };

  const createFeatureLabelMappingForCheckboxes = () => {
    const mapping = {};
    const featureLabels = {
      0: 'crash_barriers_guard_rails_damaged', 1: 'damaged_blinkers_attenuators_delineators_anti_glare',
      2: 'damaged_bus_shelters_faded_markings_damaged_signages', 3: 'damaged_lights',
      4: 'damaged_road_studs_rumble_strips_hazard_markers', 5: 'Damaged Kerb / Faded Kerb Painting',
      6: 'encroachments_illegal_parking', 7: 'faded_painting_barrier_guard_rail',
      8: 'faded_pavement_marking', 9: 'inappropriate_diversions',
      10: 'missing_assets_change_detection', 11: 'missing_poor_safety_measures',
      12: 'non_functional_lights', 13: 'overall_cleanliness',
      14: 'pavement_marking_visibility_day_night', 15: 'road_stud_rumble_strip_visibility_day_night',
      16: 'signage_visibility_day_night', 17: 'unauthorized_median_openings',
      18: 'Unauthorized Signboards / Hoardings'
    };


    const featureKeyToMarkerLabel = {

      'damaged_lights': ['damaged_lights'],
      'damaged_road_studs_rumble_strips_hazard_markers': ['damaged_road_studs_rumble_strips_hazard_markers'],
      'Damaged Kerb / Faded Kerb Painting': ['Damaged Kerb / Faded Kerb Painting'],
      'encroachments_illegal_parking': ['encroachments_illegal_parking'],
      'faded_painting_barrier_guard_rail': ['faded_painting_barrier_guard_rail'],
      'faded_pavement_marking': ['faded_pavement_marking'],
      'inappropriate_diversions': ['inappropriate_diversions'],
      'missing_assets_change_detection': ['missing_assets_change_detection'],
      'missing_poor_safety_measures': ['missing_poor_safety_measures'],
      'non_functional_lights': ['non_functional_lights'],
      'overall_cleanliness': ['overall_cleanliness'],
      'pavement_marking_visibility_day_night': ['pavement_marking_visibility_day_night'],
      'road_stud_rumble_strip_visibility_day_night': ['road_stud_rumble_strip_visibility_day_night'],
      'signage_visibility_day_night': ['signage_visibility_day_night'],

      'Unauthorized Signboards / Hoardings': ['Unauthorized Signboards / Hoardings']
    };

    Object.keys(roadsegmentOptions).forEach(key => {
      const index = parseInt(key);
      if (index > 0) {
        const featureLabel = featureLabels[index - 1];
        const markerLabels = featureKeyToMarkerLabel[featureLabel];
        mapping[roadsegmentOptions[key].label.toLowerCase()] = markerLabels;
      }
    });

    return mapping;
  };

  const filterCheckboxMarkers = useCallback(() => {
    if (isFilteringRef.current) return; // prevent re-trigger loop
    isFilteringRef.current = true;
    console.log('=== filterCheckboxMarkers called ===');
    let tempMarkers = [];
    let pointKeyArray = [];
    let pointIndexArray = [];
    let selectedDefectsWithSeverity = []; // Store {label: string, severity: string|null}

    const defectMapping = createLabelMappingForCheckboxes();
    Object.values(checkboxes).forEach((checkbox, index) => {
      if (checkbox["selected"]) {
        const mappedLabels = defectMapping[checkbox["label"].toLowerCase()];
        if (mappedLabels) pointKeyArray.push(...mappedLabels);
        pointIndexArray.push(index);

        // Extract severity from label if present (e.g., "Pothole (high)" -> severity: "high")
        const label = checkbox["label"].toLowerCase();
        let severity = null;
        if (label.includes('(high)')) {
          severity = 'high';
        } else if (label.includes('(medium)')) {
          severity = 'medium';
        } else if (label.includes('(low)')) {
          severity = 'low';
        }

        console.log(`✅ Selected: "${checkbox["label"]}" -> maps to: [${mappedLabels?.join(', ')}] (${mappedLabels?.length || 0} labels) | severity: ${severity}`);

        // Store the mapping for this selected checkbox
        mappedLabels?.forEach(mappedLabel => {
          selectedDefectsWithSeverity.push({
            label: mappedLabel,
            severity: severity
          });
        });
      }
    });

    console.log('Selected defects with severity:', selectedDefectsWithSeverity);

    // Update "All" checkbox state without triggering re-render loop
    const allCheckboxesSelected = checkIfAllPresent(Object.keys(checkboxes).length, pointIndexArray);
    if (checkboxes[0]?.selected !== allCheckboxesSelected) {
      // Use queueMicrotask to avoid synchronous state update during filter
      queueMicrotask(() => {
        setCheckboxes(prev => {
          if (prev[0]?.selected === allCheckboxesSelected) return prev;
          const temp = { ...prev };
          temp[0] = { ...temp[0], selected: allCheckboxesSelected };
          return temp;
        });
      });
    }

    let keyArray = [];
    let keyIndex = [];
    Object.values(severityOptions).forEach((checkbox, index) => {
      if (checkbox["selected"]) {
        keyArray.push(checkbox["label"].toLowerCase());
        keyIndex.push(index);
      }
    });

    console.log('Severity options selected (keyArray):', keyArray);

    const allSeveritySelected = checkIfAllPresent(Object.keys(severityOptions).length, keyIndex);
    if (severityOptions[0]?.selected !== allSeveritySelected) {
      queueMicrotask(() => {
        setSeverityOptions(prev => {
          if (prev[0]?.selected === allSeveritySelected) return prev;
          const temp = { ...prev };
          temp[0] = { ...temp[0], selected: allSeveritySelected };
          return temp;
        });
      });
    }

    let roadAssetKeyArray = [];
    const featureMapping = createFeatureLabelMappingForCheckboxes();
    Object.values(roadsegmentOptions).forEach((checkbox, index) => {
      if (checkbox["selected"]) {
        const mappedLabels = featureMapping[checkbox["label"].toLowerCase()];
        if (mappedLabels) roadAssetKeyArray.push(...mappedLabels);
      }
    });

    console.log('Total markers to filter:', markers.length);
    if (markers.length > 0 && markers[0].inference_info?.length > 0) {
      console.log('Sample marker inference_info:', markers[0].inference_info[0]);
    }

    // Build set of selected wards for fast lookup
    const hasWardFilter = selectedWardFilters.length > 0;
    const selectedWardSet = hasWardFilter ? new Set(selectedWardFilters) : null;

    // Helper: normalize a created value to YYYY-MM-DD
    const toDateStr = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toISOString().slice(0, 10);
    };

    // Helper: check if a marker passes location + survey date filters
    const passesLocationFilters = (marker) => {
      if (hasWardFilter) {
        if (!selectedWardSet.has(marker._ward || 'Unknown')) return false;
      }
      if (selectedSurveyDate) {
        if (toDateStr(marker.created) !== selectedSurveyDate) return false;
      }
      // Chainage-segment focus: hide markers outside the requested 100m bucket.
      if (chainageBounds) {
        const lat = Number(marker.latitude), lng = Number(marker.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
        if (lat < chainageBounds.minLat || lat > chainageBounds.maxLat ||
            lng < chainageBounds.minLng || lng > chainageBounds.maxLng) return false;
      }
      return true;
    };

    // Early exit if no filters are applied - show all markers
    if (pointKeyArray.length === 0 && keyArray.length === 0 && roadAssetKeyArray.length === 0 && !isRoad && !hasWardFilter && !selectedSurveyDate && selectedConditions.length === 0 && !chainageBounds) {
      const allVisible = backupMarkerState.map(marker => ({ ...marker, isvisible: true }));
      setMarkers(allVisible);
      queueMicrotask(() => { isFilteringRef.current = false; });
      return;
    }

    // If only location/condition filters are applied (no defect/severity/asset filters)
    // Map directly from backupMarkerState so no-defect markers (empty inference_info) are
    // not incorrectly hidden by the temp_num=0 path inside markers.map below.
    if (pointKeyArray.length === 0 && keyArray.length === 0 && roadAssetKeyArray.length === 0 && !isRoad) {
      const _COND_SEV_PT = { 'Poor': ['high'], 'Fair': ['medium'], 'Good': ['low', 'no defect'] };
      const allowedSevs = selectedConditions.length > 0
        ? new Set(selectedConditions.flatMap(c => _COND_SEV_PT[c] || []))
        : null;
      const _COLOR_TO_SEV_PT = { '#DC3C3C': 'high', '#E87A00': 'medium', '#C8C800': 'low', '#00BB00': 'no defect' };
      const filtered = backupMarkerState.map(marker => {
        if (!passesLocationFilters(marker)) return { ...marker, isvisible: false };
        if (allowedSevs) {
          const markerColor = getMarkerColor(marker.defect_state, false, marker.inference_info);
          const markerSev = _COLOR_TO_SEV_PT[markerColor] || 'no defect';
          return { ...marker, isvisible: allowedSevs.has(markerSev) };
        }
        return { ...marker, isvisible: true };
      });
      setMarkers(filtered);
      if (mapRef.current && window.google?.maps) {
        const visibleMarkers = filtered.filter(m => m.isvisible);
        if (visibleMarkers.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();
          visibleMarkers.forEach(m => bounds.extend({ lat: m.latitude, lng: m.longitude }));
          mapRef.current.fitBounds(bounds, { top: 80, bottom: 100, left: 50, right: 50 });
        }
      }
      queueMicrotask(() => { isFilteringRef.current = false; });
      return;
    }

    tempMarkers = markers.map((item) => {
      // Safety check for inference_info
      if (!item.inference_info || !Array.isArray(item.inference_info)) {
        item.inference_info = [];
      }
      if (!item.asset_info || !Array.isArray(item.asset_info)) {
        item.asset_info = [];
      }
      // Normalize: convert dashes to hyphens, then collapse spaces/underscores
      // so "unauthorized median openings" matches "unauthorized_median_openings"
      // and "Shoulder – Rain Cuts" matches "Shoulder - Rain Cuts"
      const normalizeLabel = (s) => s.replace(/–|−|—/g, '-').replace(/[\s_]+/g, '_');

      let temp_num = item.inference_info.filter((info) => {
        const backendLabel = info.label.toLowerCase();
        const backendLabelNorm = normalizeLabel(backendLabel);

        // Asset-inventory items are not defects — they carry no severity. Skipping
        // the color/severity derivation keeps them out of the High/Med/Low buckets
        // and prevents false partial-matches against defect color keys
        // (e.g. "Street Light" wrongly matching "missing assets (street lights)").
        const isAsset = isAssetInventoryName(info.label);

        // Derive severity for filter matching.
        // Bhilwara: use info.severity directly — color depends on label+severity combination,
        // so deriving severity from label color would give wrong results (e.g. Cracking high → orange, not red).
        // Default: derive from label color first (keeps filter ↔ visual dot color in sync).
        let infoSeverity = null;
        if (isAsset) {
          infoSeverity = null;
        } else if (isBhilwaraUser) {
          infoSeverity = _normSeverity(info.severity || '');
          if (infoSeverity === 'none') infoSeverity = null;
        } else {
          const labelColor = _getLabelColor(info.label || '');
          if (labelColor === '#DC3C3C') infoSeverity = 'high';
          else if (labelColor === '#E87A00') infoSeverity = 'medium';
          else if (labelColor === '#C8C800') infoSeverity = 'low';

          // Fallback to backend severity only when label color is unknown
          if (!infoSeverity) {
            infoSeverity = info.severity ? info.severity.toLowerCase() : null;
            if (infoSeverity) {
              if (infoSeverity.includes('high') || infoSeverity === 'h' || infoSeverity === '3') {
                infoSeverity = 'high';
              } else if (infoSeverity.includes('medium') || infoSeverity === 'm' || infoSeverity === '2') {
                infoSeverity = 'medium';
              } else if (infoSeverity.includes('low') || infoSeverity === 'l' || infoSeverity === '1') {
                infoSeverity = 'low';
              }
            }
          }
        }

        // If we have selected defects with specific severity requirements
        if (selectedDefectsWithSeverity.length > 0) {
          const matches = selectedDefectsWithSeverity.some(defectFilter => {
            // Use exact match for specific labels to avoid substring matching issues
            // e.g., "encroachment" should not match "encroachments_illegal_parking"
            const filterLabelLower = defectFilter.label.toLowerCase();
            const filterLabelNorm = normalizeLabel(filterLabelLower);
            let labelMatches;

            // // For exact match labels like "Encroachment", use strict equality
            // if (filterLabelLower === 'encroachment') {
            //   labelMatches = backendLabel === filterLabelLower;
            // } else {
            //   // For other labels, use contains logic
            //   labelMatches = backendLabel === filterLabelLower ||
            //     backendLabel.includes(filterLabelLower);
            // }
            // For exact match labels like "Encroachment", use strict equality
            if (filterLabelNorm === 'encroachment') {
              labelMatches = backendLabelNorm === filterLabelNorm;
            } else {
              // For other labels, use contains logic (normalized to handle space vs underscore)
              labelMatches = backendLabelNorm === filterLabelNorm ||
                backendLabelNorm.includes(filterLabelNorm);
            }

            // If the filter has a severity requirement, check it matches
            if (defectFilter.severity !== null) {
              const severityMatches = infoSeverity === defectFilter.severity;
              const result = labelMatches && severityMatches;

              // Debug logging
              if (labelMatches) {
                console.log(`Checking: ${backendLabel} with severity ${infoSeverity} against filter ${defectFilter.label}(${defectFilter.severity}) = ${result}`);
              }

              return result;
            } else {
              // No severity requirement, just match label
              return labelMatches;
            }
          });

          // Also check if severity filter is applied separately
          if (keyArray.length > 0 && infoSeverity && !keyArray.includes(infoSeverity)) {
            return false;
          }

          return matches;
        }

        //   // Fallback to original logic if no specific defect filters
        //   if (keyArray.length !== 0 && pointIndexArray.length !== 0) {
        //     const hasMatchingLabel = pointKeyArray.some(markerLabel => {
        //       const markerLabelLower = markerLabel.toLowerCase();
        //       // Use exact match for "Encroachment"
        //       if (markerLabelLower === 'encroachment') {
        //         return backendLabel === markerLabelLower;
        //       }
        //       return backendLabel === markerLabelLower ||
        //         backendLabel.includes(markerLabelLower);
        //     });
        //     return (hasMatchingLabel && keyArray.includes(infoSeverity));
        //   } else if (keyArray.length > 0 && pointIndexArray.length === 0) {
        //     return keyArray.includes(item.defect_state.toLowerCase());
        //   } else if (keyArray.length === 0 && pointIndexArray.length > 0) {
        //     const matches = pointKeyArray.some(markerLabel => {
        //       const markerLabelLower = markerLabel.toLowerCase();
        //       // Use exact match for "Encroachment"
        //       if (markerLabelLower === 'encroachment') {
        //         return backendLabel === markerLabelLower;
        //       }
        //       return backendLabel === markerLabelLower ||
        //         backendLabel.includes(markerLabelLower);
        //     });
        //     return matches;
        //   } else {
        //     return true;
        //   }
        // }).length;

        // Fallback to original logic if no specific defect filters
        if (keyArray.length !== 0 && pointIndexArray.length !== 0) {
          const hasMatchingLabel = pointKeyArray.some(markerLabel => {
            const markerLabelNorm = normalizeLabel(markerLabel.toLowerCase());
            if (markerLabelNorm === 'encroachment') {
              return backendLabelNorm === markerLabelNorm;
            }
            return backendLabelNorm === markerLabelNorm ||
              backendLabelNorm.includes(markerLabelNorm);
          });
          return (hasMatchingLabel && keyArray.includes(infoSeverity));
        } else if (keyArray.length > 0 && pointIndexArray.length === 0) {
          // ─── FIX v2: Severity-only filter — effective severity from both defect_state + inference_info ───
          // BEHAVIOR: "Low filter = locations whose WORST defect is low"
          //   A marker only shows under Low if its max severity across all sources equals low.
          //   If it has even one medium/high defect it will only appear in Medium/High filter.
          //
          // TO REVERT TO v1 (any matching inference_info item): uncomment the block below and
          // remove the effective-severity block that follows it.
          //
          // ── v1 (per-item check — showed mixed-severity markers in lower filter) ──
          // return keyArray.includes(infoSeverity);
          // ── end v1 ──
          //
          // ── v2: effective severity derived from getMarkerColor (visual color) ──
          // Uses the same logic as getMarkerColor so the filter matches the dot
          // color the user sees on the map. This avoids the mismatch where the
          // backend sends severity:"low" but the label maps to a red color.
          const markerColor = getMarkerColor(item.defect_state, false, item.inference_info);
          const _COLOR_TO_SEV_FILTER = { '#DC3C3C': 'high', '#E87A00': 'medium', '#C8C800': 'low', '#00BB00': 'no defect' };
          const effectiveSev = _COLOR_TO_SEV_FILTER[markerColor] ?? 'none';
          return keyArray.includes(effectiveSev);
          // ── end v2 ──
        } else if (keyArray.length === 0 && pointIndexArray.length > 0) {
          const matches = pointKeyArray.some(markerLabel => {
            const markerLabelNorm = normalizeLabel(markerLabel.toLowerCase());
            if (markerLabelNorm === 'encroachment') {
              return backendLabelNorm === markerLabelNorm;
            }
            return backendLabelNorm === markerLabelNorm ||
              backendLabelNorm.includes(markerLabelNorm);
          });
          return matches;
        } else {
          return true;
        }
      }).length;

      let asset_num = item.asset_info.filter((info) => {
        const mappedLabel = mapLabel(info.label);
        return roadAssetKeyArray.some(markerLabel =>
          mappedLabel.toLowerCase().includes(markerLabel.toLowerCase())
        );
      }).length;

      // Check location filters (road + ward) - if active and marker doesn't match, hide it
      if (!passesLocationFilters(item)) {
        return { ...item, isvisible: false };
      }

      // No-defect markers always produce temp_num=0 (empty inference_info means the
      // filter callback never runs). Use getMarkerColor — the same logic the map
      // renders — so the filter matches exactly what the user sees as green dots.
      if (keyArray.length > 0 && pointIndexArray.length === 0) {
        const markerColor = getMarkerColor(item.defect_state, false, item.inference_info);
        if (markerColor === '#00BB00') {
          return { ...item, isvisible: keyArray.includes('no defect') };
        }
      }

      return (temp_num && (roadAssetKeyArray.length === 0 || asset_num))
        ? { ...item, isvisible: true }
        : { ...item, isvisible: false };
    });

    // Condition top-bar filter for points (combined with severity/defect filters):
    // Good → low + no defect, Fair → medium, Poor → high.
    // Intersects with severity result: marker must pass BOTH.
    if (selectedConditions.length > 0) {
      const _COND_SEV_PT = { 'Poor': ['high'], 'Fair': ['medium'], 'Good': ['low', 'no defect'] };
      const allowedSevs = new Set(selectedConditions.flatMap(c => _COND_SEV_PT[c] || []));
      const _COLOR_TO_SEV_PT = { '#DC3C3C': 'high', '#E87A00': 'medium', '#C8C800': 'low', '#00BB00': 'no defect' };
      tempMarkers = tempMarkers.map(item => {
        const markerColor = getMarkerColor(item.defect_state, false, item.inference_info);
        const markerSev = _COLOR_TO_SEV_PT[markerColor] || 'no defect';
        return { ...item, isvisible: item.isvisible && allowedSevs.has(markerSev) };
      });
    }

    if (!isRoad) {
      const visibleCount = tempMarkers.filter(m => m.isvisible).length;
      console.log(`📍 Filter result: ${visibleCount} of ${tempMarkers.length} markers visible`);
      console.log(`   Searched labels: ${Array.from(new Set(pointKeyArray)).join(', ')}`);
      setMarkers(tempMarkers);

      // Auto-pan/zoom map to fit visible markers when any filter is active
      const hasActiveFilter = keyArray.length > 0 || pointIndexArray.length > 0 || hasWardFilter;
      if (hasActiveFilter && mapRef.current && window.google?.maps) {
        const visibleMarkers = tempMarkers.filter(m => m.isvisible);
        if (visibleMarkers.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();
          visibleMarkers.forEach(m => bounds.extend({ lat: m.latitude, lng: m.longitude }));
          mapRef.current.fitBounds(bounds, { top: 80, bottom: 100, left: 50, right: 50 });
        }
      }
    }
    // Allow next filter call after this render cycle completes
    queueMicrotask(() => { isFilteringRef.current = false; });
  }, [checkboxes, severityOptions, roadsegmentOptions, isRoad, backupMarkerState, selectedWardFilters, selectedSurveyDate, getMarkerColor, selectedConditions, isBhilwaraUser, chainageBounds]);

  // Debounced filter effect — runs when filter options change or new markers arrive
  useEffect(() => {
    if (backupMarkerState.length === 0) return;

    const timeoutId = setTimeout(() => {
      startTransition(() => {
        filterCheckboxMarkers();
      });
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [checkboxes, severityOptions, roadsegmentOptions, selectedWardFilters, selectedSurveyDate, filterCheckboxMarkers, backupMarkerState.length]);

  const handleEnroachChange = (event, index) => {
    setIsRoad(false);
    let temp = { ...segmentOptions };
    Object.keys(temp).forEach((key) => { temp[key]["selected"] = false; });
    setSegmentOptions(temp);
    if (index === 0 && event.target.checked) setAllTrue("severity");
    else if (index === 0 && !event.target.checked) setAllFalse("severity");
    if (index !== 0) {
      const temp = { ...severityOptions };
      temp[index]["selected"] = !temp[index]["selected"];
      setSeverityOptions(temp);
    }
  };

  const handleFeatureCheckboxChange = (index) => (event) => {
    const temp = { ...roadsegmentOptions };
    if (index === 0) {
      temp[index]["selected"] = event.target.checked;
      Object.keys(temp).forEach((key) => { temp[key]["selected"] = event.target.checked; });
    } else {
      temp[index]["selected"] = event.target.checked;
      if (Object.values(temp).slice(1).every((option) => option.selected)) temp[0].selected = true;
      else temp[0].selected = false;
    }
    setRoadSegmentOptions(temp);
  };

  function containsMultipleIds(listOfIds) {
    try {
      const ids = JSON.parse(listOfIds);
      return Array.isArray(ids) && ids.length > 1;
    } catch (error) {
      return false;
    }
  }
  const listOfIds = searchParams.get("listOfIds");

  const menus = [
    {
      name: "Map",
      link: `${window.location.origin.toString()}/pavement/multiMap?listOfIds=` + searchParams.get("listOfIds"),
      icon: IoMapSharp,
    },
    {
      name: "Report",
      link: containsMultipleIds(listOfIds) ? "#" : `${window.location.origin.toString()}/pavement/report?listOfIds=${listOfIds}`,
      icon: RiArticleFill,
      onClick: containsMultipleIds(listOfIds) ? (event) => { event.preventDefault(); alert("Please select only one project."); } : undefined,
    },
    {
      name: "Video Inspection",
      link: containsMultipleIds(listOfIds) ? "#" : `${window.location.origin.toString()}/pavement/video?listOfIds=${listOfIds}`,
      icon: PiVideoDuotone,
      onClick: containsMultipleIds(listOfIds) ? (event) => { event.preventDefault(); alert("Please select only one project."); } : undefined,
    },
    {
      name: "Notifications",
      link: `${window.location.origin.toString()}/pavement/notifications?listOfIds=` + searchParams.get("listOfIds"),
      icon: RiNotification3Fill,
    },
    {
      name: "VersionControl",
      link: "#",
      icon: RiHistoryFill,
      onClick: (event) => { event.preventDefault(); alert("Only Available For Enterprise Customers!"); },
    },
    {
      name: "Budget Calculator",
      link: `${window.location.origin.toString()}/pavement/calculator?listOfIds=` + searchParams.get("listOfIds"),
      icon: RiCalculatorFill,
    },
    {
      name: "Download Report",
      link: `${window.location.origin.toString()}/pavement/multireport?listOfIds=` + searchParams.get("listOfIds"),
      icon: RiFileDownloadFill,
    },
    {
      name: "Traffic Management System",
      link: `${window.location.origin.toString()}/pavement/traffic?listOfIds=` + searchParams.get("listOfIds"),
      icon: RiTrafficLightLine,
    },
  ];

  const whiteMapStyles = [
    { elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#333333" }] },
    { featureType: "poi", elementType: "labels", stylers: [{ visibility: "on" }] },
    { featureType: "transit", elementType: "labels", stylers: [{ visibility: "on" }] },
    { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#c9c9c9" }] },
    { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#d9d9d9" }] },
    { featureType: "road", elementType: "labels", stylers: [{ visibility: "on" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#d0d0d0" }] },
    { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#e0e0e0" }] }
  ];

  const darkMapStyles = [
    { elementType: "geometry", stylers: [{ color: "#000000" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#808080" }] },
    { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "administrative", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "landscape", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "poi.park", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "water", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#404040" }] },
    { featureType: "road", elementType: "labels", stylers: [{ visibility: "on", color: "#f9f9f9" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#404040" }] },
    { featureType: "road.highway", elementType: "labels", stylers: [{ visibility: "on", color: "#f9f9f9" }] }
  ];

  const mapStyles = mapStyleMode === 'white' ? whiteMapStyles : darkMapStyles;

  const RoadRatingGauge = ({ rating }) => (
    <GaugeChart
      id="gauge-chart1"
      nrOfLevels={10}
      needleColor="#000000"
      arcPadding={0.02}
      textColor="#000000"
      percent={(rating || 0) / 100}
      colors={['red', 'orange', 'green']}
      style={{ margin: 'auto', width: '60%', height: '60%' }}
    />
  );

  // Navigation helpers
  const goToPreviousMarker = () => {
    const visibleMarkers = markers.filter(m => m.isvisible);
    if (visibleMarkers.length === 0) return;
    setCurrentMarkerIndex(prevIndex => {
      // Find current position in visible markers
      const currentVisibleIdx = visibleMarkers.findIndex((m, i) => {
        const originalIdx = markers.indexOf(m);
        return originalIdx === prevIndex;
      });
      const prevVisibleIdx = currentVisibleIdx > 0 ? currentVisibleIdx - 1 : visibleMarkers.length - 1;
      const targetMarker = visibleMarkers[prevVisibleIdx];
      const newIndex = markers.indexOf(targetMarker);
      triggerMarkerClick(newIndex);
      return newIndex;
    });
  };
  const goToNextMarker = () => {
    const visibleMarkers = markers.filter(m => m.isvisible);
    if (visibleMarkers.length === 0) return;
    setCurrentMarkerIndex(prevIndex => {
      const currentVisibleIdx = visibleMarkers.findIndex((m, i) => {
        const originalIdx = markers.indexOf(m);
        return originalIdx === prevIndex;
      });
      const nextVisibleIdx = currentVisibleIdx < visibleMarkers.length - 1 ? currentVisibleIdx + 1 : 0;
      const targetMarker = visibleMarkers[nextVisibleIdx];
      const newIndex = markers.indexOf(targetMarker);
      triggerMarkerClick(newIndex);
      return newIndex;
    });
  };
  const triggerMarkerClick = (index) => {
    const marker = markers[index];
    if (marker) {
      handleMarkerClick(
        marker.og_file,
        deriveAiFile(marker),
        marker.inference_info.length,
        marker.inference_info,
        marker.latitude,
        marker.longitude,
        marker.NEW_CODEBUILD_BUILD_ID,
        marker.created,
        marker.markerId,
        marker.markerIndex,
        index,
        marker.gcs_bucket
      );
    }
  };

  const toggleAutoPlay = () => {
    if (isAutoPlay) {
      clearInterval(autoPlayIntervalId);
    } else {
      startAutoPlay();
    }
    setIsAutoPlay(prev => !prev);
  };

  const startAutoPlay = () => {
    const intervalId = setInterval(() => {
      goToNextMarker();
    }, 3000);
    setAutoPlayIntervalId(intervalId);
  };

  const playPause = isAutoPlay ? (<FcNoVideo onClick={toggleAutoPlay} />) : (<FcVideoCall onClick={toggleAutoPlay} />);

  // Memoize marker scale to debounce zoom changes
  const markerScale = useMemo(() => calculateScale(zoomLevel), [zoomLevel]);


  // Build Supercluster index from visible markers for zoom-based clustering
  const superclusterIndex = useMemo(() => {
    const visibleMarkers = markers.filter(m => m.isvisible);
    if (visibleMarkers.length === 0) return null;
    const sc = new Supercluster({ radius: 80, maxZoom: 15, minPoints: 2 });
    const points = visibleMarkers.map((m) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [m.longitude, m.latitude] },
      properties: {
        markerIndex: markers.indexOf(m),
        defect_state: m.defect_state,
        marker: m,
      },
    }));
    sc.load(points);
    return sc;
  }, [markers]);

  // Compute cluster/point data for the current zoom level
  const clusterData = useMemo(() => {
    if (!superclusterIndex) return [];
    const zoom = Math.max(0, Math.min(Math.round(zoomLevel), 20));
    const raw = superclusterIndex.getClusters([-180, -85, 180, 85], zoom);
    return raw.map(c => {
      if (c.properties.cluster) {
        const leaves = superclusterIndex.getLeaves(c.properties.cluster_id, Infinity);
        let high = 0, medium = 0;
        leaves.forEach(l => {
          if (l.properties.defect_state === 'high') high++;
          else if (l.properties.defect_state === 'medium') medium++;
        });
        return {
          position: c.geometry.coordinates,
          count: c.properties.point_count,
          isCluster: true,
          clusterId: c.properties.cluster_id,
          dominantSeverity: high > 0 ? 'high' : medium > 0 ? 'medium' : 'low',
        };
      }
      return {
        position: c.geometry.coordinates,
        count: 1,
        isCluster: false,
        markerIndex: c.properties.markerIndex,
        defect_state: c.properties.defect_state,
        marker: c.properties.marker,
      };
    });
  }, [superclusterIndex, zoomLevel]);

  // Build polyline segments colored by 100m bin mode severity
  // Step 1: bin all visible markers into ~100m spatial cells, compute mode per bin
  // Step 2: sort by markerIndex, connect consecutive points ≤100m apart
  // Step 3: each segment colored by its starting point's bin mode
  const defectSegments = useMemo(() => {
    const visible = markers.filter(m => m.isvisible);
    if (visible.length < 2) return [];

    const BIN = 0.0009; // ~100m in degrees

    // Effective severity mirrors getMarkerColor exactly so segment colors match dot
    // colors — same mode (most-frequent severity) logic, defect_state fallback, and
    // Bhilwara handling. Asset/neutral/green colors map to 'none' (excluded from bin mode).
    const _COLOR_TO_SEV = { '#DC3C3C': 'high', '#E87A00': 'medium', '#C8C800': 'low' };
    const effectiveSeverity = (m) => {
      const color = getMarkerColor(m.defect_state, false, m.inference_info);
      return _COLOR_TO_SEV[color] || 'none';
    };

    // Build bin mode map
    const bins = {};
    for (const m of visible) {
      const key = `${Math.round(m.latitude / BIN)},${Math.round(m.longitude / BIN)}`;
      if (!bins[key]) bins[key] = {};
      const s = effectiveSeverity(m);
      bins[key][s] = (bins[key][s] || 0) + 1;
    }
    const binMode = {};
    for (const [key, tally] of Object.entries(bins)) {
      // Only count actual defect frames (exclude 'none') for mode
      // If no defect frames at all → green; otherwise mode of defect types only
      const defectEntries = Object.entries(tally).filter(([s]) => s !== 'none');
      binMode[key] = defectEntries.length > 0
        ? defectEntries.reduce((best, [s, cnt]) => {
          if (cnt > best.cnt) return { s, cnt };
          return best;
        }, { s: 'none', cnt: 0 }).s
        : 'none';
    }

    const distM = (lat1, lng1, lat2, lng2) => {
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLng = (lng2 - lng1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
      return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    const severityColor = (s) => {
      if (s === 'high') return [220, 55, 55];
      if (s === 'medium') return [230, 115, 0];
      if (s === 'low') return [220, 190, 0];
      return [0, 175, 0];
    };

    const sorted = [...visible].sort((a, b) => a.markerIndex - b.markerIndex);
    const segments = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      const p = sorted[i];
      const q = sorted[i + 1];
      if (distM(p.latitude, p.longitude, q.latitude, q.longitude) > 100) continue;
      // Color by mode of p's 100m bin
      const key = `${Math.round(p.latitude / BIN)},${Math.round(p.longitude / BIN)}`;
      const severity = binMode[key] || 'none';
      segments.push({
        path: [[p.longitude, p.latitude], [q.longitude, q.latitude]],
        color: severityColor(severity),
        severity,
      });
    }
    return segments;
  }, [markers, isBhilwaraUser, getMarkerColor]);

  // Convert marker colors to RGB format for deck.gl
  const getRGBColor = useCallback((hexColor) => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return [r, g, b];
  }, []);

  // Enrich traffic congestion points with nearby defects for Google Maps markers.
  // At low zoom, sample down so the overview isn't cluttered; all points appear as user zooms in.
  const trafficPointsEnriched = useMemo(() => {
    if (!showTrafficCongestion || trafficCongestionData.length === 0) return [];
    const total = trafficCongestionData.length;
    const targetCount = zoomLevel <= 12 ? Math.min(total, 5)
      : zoomLevel <= 13 ? Math.min(total, 8)
      : zoomLevel <= 14 ? Math.min(total, Math.ceil(total / 3))
      : zoomLevel <= 15 ? Math.min(total, Math.ceil(total / 2))
      : total;
    const stride = targetCount > 0 ? total / targetCount : 1;
    const sampled = targetCount >= total
      ? trafficCongestionData
      : Array.from({ length: targetCount }, (_, i) => trafficCongestionData[Math.floor(i * stride)]);
    return sampled.map(tp => {
      const nearbyDefects = markers.filter(m => {
        if (!m.isvisible || !m.inference_info || m.inference_info.length === 0) return false;
        const dist = _haversineKm(tp.latitude, tp.longitude, Number(m.latitude), Number(m.longitude));
        return dist <= 0.05; // 50 meters
      });
      const defectLabels = [...new Set(nearbyDefects.flatMap(m => (m.inference_info || []).map(i => i.label)))];
      return { ...tp, nearbyDefects: nearbyDefects.length, defectLabels };
    });
  }, [showTrafficCongestion, trafficCongestionData, markers, zoomLevel]);

  // Icon mapping for deck.gl IconLayer
  const ICON_MAPPING = {
    marker: {
      x: 0,
      y: 0,
      width: 128,
      height: 128,
      mask: true
    }
  };

  // Create deck.gl data from markers
  const deckGLData = useMemo(() => {
    const visibleMarkers = [];
    const len = markers.length;

    for (let i = 0; i < len; i++) {
      const marker = markers[i];
      if (!marker.isvisible) continue;

      const isSelected = currentMarkerIndex === i;

      visibleMarkers.push({
        position: [marker.longitude, marker.latitude],
        color: getRGBColor(isSelected ? '#00000000' : getMarkerColor(marker.defect_state, false, marker.inference_info)),
        radius: isSelected ? 0 : 5,
        isSelected: isSelected,
        markerIndex: i,
        originalMarker: marker
      });
    }

    return visibleMarkers;
  }, [markers, currentMarkerIndex, getMarkerColor, getRGBColor]);

  // Update deck.gl layers based on display mode and zoom level
  useEffect(() => {
    if (!overlayRef.current) return;

    const layers = [];

    if (mapDisplayMode === 'heatmap') {
      // Density heatmap — useful when zoomed out for concentration overview
      const visibleMarkers = markers.filter(m => m.isvisible);
      layers.push(new HeatmapLayer({
        id: 'heatmap-layer',
        data: visibleMarkers,
        getPosition: d => [d.longitude, d.latitude],
        getWeight: d => d.defect_state === 'high' ? 3 : d.defect_state === 'medium' ? 2 : 1,
        radiusPixels: 40,
        intensity: 1,
        threshold: 0.03,
        colorRange: [
          [65, 182, 196, 100],
          [127, 205, 187, 160],
          [199, 233, 180, 190],
          [255, 153, 0, 220],
          [228, 64, 64, 255],
        ],
      }));
    } else if (mapDisplayMode === 'defects') {
      // Layer order matters: later layers draw on top. Push the road-condition path
      // segments FIRST (overview context) so the individual point dots render above them.
      if (zoomLevel < 16) {
        // Low/medium zoom — polyline segments between consecutive survey points ≤100m apart.
        // At overview (zoom < 13) suppress low/none to reduce noise —
        // BUT if the user has actively selected a severity filter, respect it and show all.
        const hasSeverityFilter = Object.values(severityOptions).slice(1).some(s => s.selected);
        const selectedSeverities = Object.values(severityOptions).slice(1)
          .filter(s => s.selected)
          .map(s => s.label.toLowerCase()); // e.g. ['low', 'no defect']

        const zoomFiltered = (zoomLevel < 15 && !hasSeverityFilter && selectedConditions.length === 0)
          ? defectSegments.filter(s => s.severity === 'high' || s.severity === 'medium')
          : defectSegments;

        const visibleSegs = hasSeverityFilter
          ? zoomFiltered.filter(s => {
            const segSev = s.severity === 'none' ? 'no defect' : s.severity;
            return selectedSeverities.includes(segSev);
          })
          : zoomFiltered;

        // Condition top-bar filter: Poor → high, Fair → medium, Good → low + none
        const _COND_SEV = { 'Poor': ['high'], 'Fair': ['medium'], 'Good': ['low', 'none'] };
        const conditionSegs = selectedConditions.length > 0
          ? visibleSegs.filter(s => selectedConditions.some(c => (_COND_SEV[c] || []).includes(s.severity)))
          : visibleSegs;

        if (conditionSegs.length > 0) {
          layers.push(new PathLayer({
            id: 'defect-segments-layer',
            data: conditionSegs,
            pickable: false,
            widthUnits: 'pixels',
            getWidth: zoomLevel >= 13 ? 4 : 3,
            getPath: d => d.path,
            getColor: d => [...d.color, 230],
            capRounded: true,
            jointRounded: true,
          }));
        }
      }

      // Individual defect points — rendered at EVERY zoom level so the overview is
      // never blank (previously points only appeared at zoom ≥ 16, leaving sparse
      // surveys / asset points invisible until the user zoomed in). Dots are sized
      // in pixels so they stay readable when zoomed out, and draw above the segments.
      {
        const isOverview = zoomLevel < 16;
        layers.push(new ScatterplotLayer({
          id: 'markers-layer',
          data: deckGLData,
          pickable: true,
          opacity: 0.92,
          stroked: true,
          filled: true,
          radiusUnits: 'pixels',
          radiusScale: 1,
          radiusMinPixels: isOverview ? 3.5 : 5,
          radiusMaxPixels: isOverview ? 6 : 9,
          lineWidthMinPixels: isOverview ? 0.5 : 1,
          lineWidthUnits: 'pixels',
          getPosition: d => d.position,
          getRadius: d => d.radius,
          getFillColor: d => [...d.color, 220],
          getLineColor: d => d.isSelected ? [0, 0, 0, 180] : [255, 255, 255, 120],
          getLineWidth: 1,
          onClick: (info) => {
            if (info.object) {
              const marker = info.object.originalMarker;
              const markerIndex = info.object.markerIndex;
              const inferenceInfo = marker.inference_info || [];
              handleMarkerClick(
                marker.og_file,
                deriveAiFile(marker),
                inferenceInfo.length,
                inferenceInfo,
                marker.latitude,
                marker.longitude,
                marker.NEW_CODEBUILD_BUILD_ID,
                marker.created,
                marker.markerId,
                marker.markerIndex,
                markerIndex,
                marker.gcs_bucket
              );
            }
          },
          onHover: (info) => {
            if (mapRef.current) {
              mapRef.current.getDiv().style.cursor = info.object ? 'pointer' : 'default';
            }
          },
          updateTriggers: {
            getRadius: [currentMarkerIndex, zoomLevel],
            getFillColor: [currentMarkerIndex],
          },
        }));
      }
    }
    // mapDisplayMode === 'road_condition': no deck.gl layers, polylines remain visible

    // Traffic congestion markers are rendered as native Google Maps <Marker> components in JSX below

    overlayRef.current.setProps({ layers });
  }, [deckGLData, clusterData, defectSegments, currentMarkerIndex, handleMarkerClick, mapDisplayMode, zoomLevel, markers, superclusterIndex, mapStyleMode, severityOptions, selectedConditions]);

  const spiderLines = [];

  // --- Render ---
  return (
    <div className="w-full h-screen flex flex-col overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Non-blocking loading indicator — map is visible behind it */}
      {!markersLoaded && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[100] bg-white/90 backdrop-blur-sm shadow-lg rounded-full px-5 py-2.5 flex items-center gap-3">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm font-medium text-gray-700">Loading markers...</span>
          {markers.length > 0 && (
            <span className="text-xs text-gray-500">({markers.filter(m => m.isvisible).length} loaded)</span>
          )}
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-full mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left section */}
            <div></div>

            {/* Right section: controls */}
            <div className="flex items-center gap-3">
              {/* Map Display Mode Toggle */}
              {/* <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden bg-white shadow-sm" title="Switch map display mode">
                {[
                  // { mode: 'road_condition', label: 'Roads' },
                  { mode: 'defects', label: 'Defects' },
                  // { mode: 'heatmap', label: 'Heatmap' },
                ].map(({ mode, label }) => (
                  <button
                    key={mode}
                    onClick={() => setMapDisplayMode(mode)}
                    className={`px-3 py-2 text-sm font-medium transition-colors border-r border-gray-300 last:border-r-0 ${mapDisplayMode === mode
                        ? 'bg-slate-800 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    {label}
                  </button>
                ))}
              </div> */}
              <button
                onClick={handleOverviewClick}
                className="px-4 py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 font-medium text-sm transition-all duration-200 flex items-center gap-2 border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300"
                title="View all markers on map"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                <span className="hidden sm:inline">Overview</span>
              </button>
              <button
                onClick={() => setMapStyleMode(mapStyleMode === 'white' ? 'dark' : 'white')}
                className="px-4 py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 font-medium text-sm transition-all duration-200 flex items-center gap-2 border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300"
                title={mapStyleMode === 'white' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
              >
                {mapStyleMode === 'white' ? (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                    </svg>
                    <span className="hidden sm:inline">Dark</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                    </svg>
                    <span className="hidden sm:inline">Light</span>
                  </>
                )}
              </button>
              <button
                onClick={toggleFullScreen}
                className="px-4 py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 font-medium text-sm transition-all duration-200 flex items-center gap-2 border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300"
                title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
              >
                {isFullScreen ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                    </svg>
                    <span className="hidden sm:inline">Exit</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                    </svg>
                    <span className="hidden sm:inline">Full Screen</span>
                  </>
                )}
              </button>
              {/* <ProfileMenu /> */}
            </div>
          </div>
        </div>
      </header>


      {/* Top Filter Bar */}
      <div className="bg-white border-b border-gray-200 z-40 shadow-sm">

        {/* Filter buttons row */}
        <div className="px-4 py-2 flex items-center gap-2 flex-wrap">
          {/* Filter icon */}
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
          </svg>

          {/* Road Name */}
          <div className="relative" ref={roadNameDropdownRef}>
            <button
              disabled
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 border border-gray-200 rounded-full text-gray-400 cursor-not-allowed focus:outline-none"
            >
              Road Name
              {selectedRoadNames.length > 0 && <span className="bg-gray-800 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none font-semibold">{selectedRoadNames.length}</span>}
              <svg className={`w-3 h-3 transition-transform ${roadNameDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {roadNameDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1">
                {ROAD_NAME_OPTIONS.map(opt => (
                  <label key={opt} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
                    <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600" checked={selectedRoadNames.includes(opt)} onChange={() => toggleTopFilter(setSelectedRoadNames, opt)} />
                    {opt}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Authority */}
          <div className="relative" ref={authorityDropdownRef}>
            <button
              disabled
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 border border-gray-200 rounded-full text-gray-400 cursor-not-allowed focus:outline-none"
            >
              Authority
              {selectedAuthorities.length > 0 && <span className="bg-gray-800 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none font-semibold">{selectedAuthorities.length}</span>}
              <svg className={`w-3 h-3 transition-transform ${authorityDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {authorityDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-40 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1">
                {AUTHORITY_OPTIONS.map(opt => (
                  <label key={opt} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
                    <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600" checked={selectedAuthorities.includes(opt)} onChange={() => toggleTopFilter(setSelectedAuthorities, opt)} />
                    {opt}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Ward */}
          {isKhammamUser && wardOptions.length > 0 && (
            <div className="relative" ref={wardDropdownRef}>
              <button
                onClick={() => setWardDropdownOpen(o => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-full text-gray-700 hover:border-gray-500 transition-colors focus:outline-none"
              >
                <svg className="w-3.5 h-3.5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                Ward
                {selectedWardFilters.length > 0 && (
                  <span className="bg-gray-800 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none font-semibold">{selectedWardFilters.length}</span>
                )}
                <svg className={`w-3 h-3 transition-transform ${wardDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {wardDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-80 flex flex-col">
                  <div className="p-2 border-b border-gray-100">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search ward..."
                        value={wardSearchTerm}
                        onChange={(e) => setWardSearchTerm(e.target.value)}
                        className="w-full px-3 py-1.5 pl-8 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                      <svg className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="overflow-y-auto flex-1 p-2">
                    <FormGroup className="space-y-0.5">
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedWardFilters.length === wardOptions.length && wardOptions.length > 0}
                            indeterminate={selectedWardFilters.length > 0 && selectedWardFilters.length < wardOptions.length}
                            onChange={() => handleWardFilterToggle('__ALL__')}
                            sx={{ '& .MuiSvgIcon-root': { fontSize: 16 }, '&.Mui-checked': { color: '#0d9488' }, '&.MuiCheckbox-indeterminate': { color: '#0d9488' } }}
                          />
                        }
                        label={<span className="text-xs font-semibold text-slate-700">Select All</span>}
                        className="ml-0"
                      />
                      {filteredWardOptions.length > 0 ? filteredWardOptions.map((ward, idx) => {
                        const count = backupMarkerState.filter(m => m._ward === ward).length;
                        return (
                          <FormControlLabel
                            key={`ward-${idx}`}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedWardFilters.includes(ward)}
                                onChange={() => handleWardFilterToggle(ward)}
                                sx={{ '& .MuiSvgIcon-root': { fontSize: 16 }, '&.Mui-checked': { color: '#0d9488' } }}
                              />
                            }
                            label={
                              <span className="text-xs text-slate-700 flex items-center gap-1">
                                {ward}
                                <span className="text-[10px] text-slate-400">({count})</span>
                              </span>
                            }
                            className="ml-0"
                          />
                        );
                      }) : (
                        <div className="text-xs text-slate-500 text-center py-2">No wards found</div>
                      )}
                    </FormGroup>
                  </div>
                  {selectedWardFilters.length > 0 && (
                    <div className="p-2 border-t border-gray-100">
                      <button
                        onClick={() => setSelectedWardFilters([])}
                        className="w-full py-1 px-3 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-md transition-colors"
                      >
                        Clear Ward Filter
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Road Type */}
          <div className="relative" ref={roadTypeDropdownRef}>
            <button
              disabled
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 border border-gray-200 rounded-full text-gray-400 cursor-not-allowed focus:outline-none"
            >
              Road Type
              {selectedRoadTypes.length > 0 && <span className="bg-gray-800 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none font-semibold">{selectedRoadTypes.length}</span>}
              <svg className={`w-3 h-3 transition-transform ${roadTypeDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {roadTypeDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1">
                {ROAD_TYPE_OPTIONS.map(opt => (
                  <label key={opt} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
                    <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600" checked={selectedRoadTypes.includes(opt)} onChange={() => toggleTopFilter(setSelectedRoadTypes, opt)} />
                    {opt}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Condition */}
          <div className="relative" ref={conditionDropdownRef}>
            <button
              onClick={() => setConditionDropdownOpen(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-full text-gray-700 hover:border-gray-500 transition-colors focus:outline-none"
            >
              Road Condition
              {selectedConditions.length > 0 && <span className="bg-gray-800 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none font-semibold">{selectedConditions.length}</span>}
              <svg className={`w-3 h-3 transition-transform ${conditionDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {conditionDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-40 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1">
                {CONDITION_OPTIONS.map(opt => (
                  <label key={opt} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
                    <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600" checked={selectedConditions.includes(opt)} onChange={() => toggleTopFilter(setSelectedConditions, opt)} />
                    {opt}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Survey Date */}
          <div className="relative" ref={surveyDateRef}>
            <button
              onClick={() => setSurveyDateOpen(prev => !prev)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-full text-gray-700 hover:border-gray-500 transition-colors focus:outline-none"
            >
              Survey Date
              {selectedSurveyDate && <span className="bg-gray-800 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center leading-none font-semibold">1</span>}
              <svg className={`w-3 h-3 transition-transform ${surveyDateOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {surveyDateOpen && (
              <div className="absolute top-full left-0 mt-1 w-44 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1 max-h-52 overflow-y-auto">
                {uniqueSurveyDates.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-400">No dates available</div>
                ) : (
                  uniqueSurveyDates.map(date => {
                    const isSelected = date === selectedSurveyDate;
                    const label = new Date(date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    return (
                      <div
                        key={date}
                        role="button"
                        onClick={() => { setSelectedSurveyDate(isSelected ? '' : date); setSurveyDateOpen(false); }}
                        className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 text-blue-700 font-medium' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        <span>{label}</span>
                        {isSelected && (
                          <svg className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Traffic Congestion Toggle */}
          {trafficCongestionData.length > 0 && (
            <button
              onClick={() => { setShowTrafficCongestion(v => !v); setSelectedTrafficPoint(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-full transition-colors focus:outline-none ${showTrafficCongestion
                  ? 'bg-amber-500 border-amber-500 text-white font-medium'
                  : 'bg-white border-gray-300 text-gray-700 hover:border-gray-500'
                }`}
            >
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              Traffic Congestion
              <span className={`text-xs rounded-full px-1.5 py-0.5 leading-none font-semibold ${showTrafficCongestion ? 'bg-white text-amber-600' : 'bg-amber-100 text-amber-700'}`}>
                {trafficCongestionData.length}
              </span>
            </button>
          )}

          {/* Clear top filters */}
          {hasTopFilters && (
            <button
              onClick={clearTopFilters}
              className="ml-auto flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              Clear filters
            </button>
          )}
        </div>{/* end filter buttons row */}

        {/* Active filter chips row */}
        {hasTopFilters && (
          <div className="px-4 py-1.5 flex items-center gap-1.5 flex-wrap border-t border-gray-100 bg-gray-50">
            <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
            </svg>
            {selectedAuthorities.map(v => (
              <span key={v} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-full font-medium">
                {v}
                <button onClick={() => toggleTopFilter(setSelectedAuthorities, v)} className="hover:text-blue-900 leading-none">×</button>
              </span>
            ))}
            {selectedRoadNames.map(v => (
              <span key={v} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-full font-medium">
                {v}
                <button onClick={() => toggleTopFilter(setSelectedRoadNames, v)} className="hover:text-blue-900 leading-none">×</button>
              </span>
            ))}
            {selectedRoadTypes.map(v => (
              <span key={v} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-full font-medium">
                {v}
                <button onClick={() => toggleTopFilter(setSelectedRoadTypes, v)} className="hover:text-blue-900 leading-none">×</button>
              </span>
            ))}
            {selectedConditions.map(v => (
              <span key={v} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-full font-medium">
                {v}
                <button onClick={() => toggleTopFilter(setSelectedConditions, v)} className="hover:text-blue-900 leading-none">×</button>
              </span>
            ))}
            {selectedWardFilters.map(v => (
              <span key={v} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-full font-medium">
                {v}
                <button onClick={() => setSelectedWardFilters(prev => prev.filter(w => w !== v))} className="hover:text-blue-900 leading-none">×</button>
              </span>
            ))}
            {selectedSurveyDate && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-full font-medium">
                Survey: {new Date(selectedSurveyDate + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                <button onClick={() => setSelectedSurveyDate('')} className="hover:text-blue-900 leading-none">×</button>
              </span>
            )}
            {showTrafficCongestion && (
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs rounded-full font-medium">
                Traffic Congestion ({trafficCongestionData.length})
                <button onClick={() => setShowTrafficCongestion(false)} className="hover:text-amber-900 leading-none">×</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Main layout */}
      <div className="flex flex-1 overflow-hidden bg-gray-50">

        {/* Map area */}
        <main className="flex-1 relative">
          <div className="absolute inset-0">
            {/* Full screen loading - only while map API loads */}
            {!isLoaded && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
                <div className="text-center">
                  <div className="w-12 h-12 border-3 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="mt-4 text-gray-600 font-medium">Initializing map...</p>
                </div>
              </div>
            )}

            {/* Zoom-level hint for defects mode */}
            {isLoaded && mapDisplayMode === 'defects' && markers.filter(m => m.isvisible).length > 0 && zoomLevel < 16 && (
              <div className={`absolute left-1/2 -translate-x-1/2 z-30 pointer-events-none transition-all duration-200 ${showDefectFilters
                  ? (Object.values(severityOptions).slice(1).some(s => s.selected) || Object.values(checkboxes).some(cb => cb.selected) ? 'bottom-36' : 'bottom-24')
                  : 'bottom-10'
                }`}>
                <div className="bg-slate-800/80 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full shadow-md">
                  Zoom in for closer detail
                </div>
              </div>
            )}

            {/* Dynamic Color Legend — toggle button + panel that opens to the left */}
            {isLoaded && (
              <div className={`absolute right-4 z-30 flex items-end gap-2 transition-all duration-200 ${showDefectFilters
                  ? (Object.values(severityOptions).slice(1).some(s => s.selected) || Object.values(checkboxes).some(cb => cb.selected) ? 'bottom-36' : 'bottom-24')
                  : 'bottom-10'
                }`}>

                {/* Legend panel — slides in from right */}
                {showLegend && (
                  <div className={`backdrop-blur-sm rounded-xl shadow-lg px-4 py-3 min-w-[170px] transition-all duration-200 ${mapStyleMode === 'dark'
                      ? 'bg-slate-800/90 border border-slate-600'
                      : 'bg-white/92 border border-gray-200'
                    }`}>

                    {/* Individual defect points (zoom ≥ 16) — show severity labels */}
                    {mapDisplayMode === 'defects' && zoomLevel >= 16 && (
                      <>
                        <p className={`text-xs font-bold mb-2 ${mapStyleMode === 'dark' ? 'text-slate-100' : 'text-slate-700'}`}>Severity</p>
                        {[
                          { color: '#DC3C3C', label: 'High' },
                          { color: '#E87A00', label: 'Medium' },
                          { color: '#C8C800', label: 'Low' },
                          { color: '#00BB00', label: 'No Defect' },
                        ].map(({ color, label }) => (
                          <div key={label} className="flex items-center gap-2 mb-1 last:mb-0">
                            <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                            <span className={`text-xs ${mapStyleMode === 'dark' ? 'text-slate-200' : 'text-slate-600'}`}>{label}</span>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Polyline overview (zoom < 16) */}
                    {mapDisplayMode === 'defects' && zoomLevel < 16 && (
                      <>
                        <p className={`text-xs font-bold mb-2 ${mapStyleMode === 'dark' ? 'text-slate-100' : 'text-slate-700'}`}>Severity</p>
                        {[
                          { color: '#DC3737', label: 'High', km: kmBySeverity.high },
                          { color: '#E67300', label: 'Medium', km: kmBySeverity.medium },
                          { color: '#DCBE00', label: 'Low', km: kmBySeverity.low },
                          { color: '#00AF00', label: 'No Defect', km: kmBySeverity.noDefect },
                        ].map(({ color, label, km }) => (
                          <div key={label} className="flex items-center gap-2 mb-1 last:mb-0">
                            <span className="w-5 h-1.5 rounded flex-shrink-0" style={{ backgroundColor: color }} />
                            <span className={`text-xs ${mapStyleMode === 'dark' ? 'text-slate-200' : 'text-slate-600'}`}>{label}</span>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Heatmap gradient scale */}
                    {mapDisplayMode === 'heatmap' && (
                      <>
                        <p className={`text-xs font-bold mb-2 ${mapStyleMode === 'dark' ? 'text-slate-100' : 'text-slate-700'}`}>Heatmap Legend</p>
                        <div
                          className="h-3 w-full rounded mb-1"
                          style={{ background: 'linear-gradient(to right, #41B6C4, #7FCDBB, #C7E9B4, #FF9900, #E44040)' }}
                        />
                        <div className={`flex justify-between text-[10px] mb-1 ${mapStyleMode === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
                          <span>Low</span>
                          <span>High</span>
                        </div>
                        <p className={`text-[10px] ${mapStyleMode === 'dark' ? 'text-slate-400' : 'text-slate-400'}`}>Defect density</p>
                      </>
                    )}

                    {/* Road condition polyline colors */}
                    {mapDisplayMode === 'road_condition' && (
                      <>
                        <p className={`text-xs font-bold mb-2 ${mapStyleMode === 'dark' ? 'text-slate-100' : 'text-slate-700'}`}>Road Condition</p>
                        {[
                          { color: '#FF0000', label: 'Poor  (≤ 40)' },
                          { color: '#FFFF00', label: 'Fair  (40 – 80)', border: true },
                          { color: '#00FF00', label: 'Good  (> 80)', border: true },
                        ].map(({ color, label, border }) => (
                          <div key={label} className="flex items-center gap-2 mb-1 last:mb-0">
                            <span
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: color, border: border ? '1px solid #bbb' : 'none' }}
                            />
                            <span className={`text-xs ${mapStyleMode === 'dark' ? 'text-slate-200' : 'text-slate-600'}`}>{label}</span>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Traffic Congestion legend */}
                    {showTrafficCongestion && (
                      <>
                        <div className={`${(mapDisplayMode === 'defects' || mapDisplayMode === 'heatmap' || mapDisplayMode === 'road_condition') ? 'mt-3 pt-2 border-t border-gray-200' : ''}`}>
                          <p className={`text-xs font-bold mb-2 ${mapStyleMode === 'dark' ? 'text-slate-100' : 'text-slate-700'}`}>Traffic Congestion</p>
                          <div className="flex items-center gap-2 mb-0">
                            <span className="w-3 h-3 flex-shrink-0" style={{ backgroundColor: '#DC3C3C', transform: 'rotate(45deg)' }} />
                            <span className={`text-xs ${mapStyleMode === 'dark' ? 'text-slate-200' : 'text-slate-600'}`}>Congestion Point</span>
                          </div>
                        </div>
                      </>
                    )}

                  </div>
                )}

                {/* Toggle button */}
                <button
                  onClick={() => setShowLegend(v => !v)}
                  className={`w-9 h-9 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 flex-shrink-0 ${mapStyleMode === 'dark'
                      ? 'bg-slate-800/90 border border-slate-600 text-slate-200 hover:bg-slate-700'
                      : 'bg-white border border-gray-200 text-slate-600 hover:bg-gray-50'
                    }`}
                  title={showLegend ? 'Hide legend' : 'Show legend'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" />
                  </svg>
                </button>

              </div>
            )}

            {/* WebGL2 unavailable — deck.gl defect/heatmap layers can't render */}
            {isLoaded && webglUnavailable && (
              <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-50 bg-amber-50 border border-amber-300 px-4 py-3 rounded-lg shadow-lg flex items-start gap-3 max-w-md">
                <RiTrafficLightLine className="text-amber-500 text-xl flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">3D defect layers unavailable</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Your browser couldn't start WebGL2, so map defect/heatmap layers are disabled.
                    Enable hardware acceleration (Settings → System → "Use graphics acceleration when available"),
                    then relaunch the browser. The base map still works.
                  </p>
                </div>
              </div>
            )}

            {/* Small loading indicator while markers load - overlayed on map */}
            {isLoaded && !webglUnavailable && markers.length === 0 && (
              <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-50 bg-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-sm">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                <div className="text-sm">
                  <p className="font-medium text-gray-700">Loading defect points...</p>
                  <p className="text-xs text-gray-500 mt-1">Fetching from server, this may take a moment</p>
                </div>
              </div>
            )}

            {isLoaded && (
              <GoogleMap
                mapContainerStyle={{
                  height: "100%",
                  width: "100%",
                }}
                center={center}
                zoom={5}
                mapId={mapId}
                onLoad={handleMapLoad}
                onIdle={handleMapIdle}
                onZoomChanged={handleZoomChanged}
                options={{
                  styles: mapStyles,
                  disableDefaultUI: true,
                  minZoom: 3,
                  maxZoom: 20,
                  zoomControl: true,
                  zoomControlOptions: { position: window.google?.maps?.ControlPosition?.RIGHT_TOP ?? 3 },
                  fullscreenControl: false,
                  streetViewControl: true,
                  mapTypeControl: false,
                  scaleControl: true,
                  rotateControl: true,
                  clickableIcons: true,
                  gestureHandling: 'greedy',
                  draggableCursor: 'pointer',
                }}
              >
                {/* Spider lines for spiderfied markers */}
                {spiderLines}

                {/* Markers are now rendered via deck.gl overlay */}

                {allInferenceList.map((inference, index) => (
                  <Polyline
                    key={index}
                    path={getPathFromInference(inference)}
                    onClick={(event) => handlePolylineClick(event, inference)}
                    options={{
                      strokeColor: getRoadColor(inference.data?.road_rating ?? inference.road_rating),
                      strokeOpacity: 0.9,
                      strokeWeight: 5,
                    }}
                  />
                ))}

                {/* Selected point marker — road engineer icon */}
                {selectedMarker.latitude !== null && selectedMarker.longitude !== null && (
                  <Marker
                    position={{ lat: Number(selectedMarker.latitude), lng: Number(selectedMarker.longitude) }}
                    icon={{
                      url: selectedPointIcon,
                      scaledSize: window.google ? new window.google.maps.Size(40, 40) : undefined,
                      anchor: window.google ? new window.google.maps.Point(20, 40) : undefined,
                    }}
                    zIndex={9999}
                  />
                )}

                {/* Traffic Congestion pin markers — native Google Maps markers (size scales with zoom) */}
                {showTrafficCongestion && (() => {
                  const trafficSize = zoomLevel <= 13 ? 36
                    : zoomLevel <= 15 ? 42
                    : zoomLevel <= 17 ? 48
                    : 56;
                  return trafficPointsEnriched.map((tp, index) => (
                    <Marker
                      key={`traffic-${index}`}
                      position={{ lat: Number(tp.latitude), lng: Number(tp.longitude) }}
                      icon={{
                        url: trafficSignalIcon,
                        scaledSize: window.google ? new window.google.maps.Size(trafficSize, trafficSize) : undefined,
                        anchor: window.google ? new window.google.maps.Point(trafficSize / 2, trafficSize / 2) : undefined,
                      }}
                      title={`Traffic Congestion: ${tp.address || `${tp.latitude}, ${tp.longitude}`}`}
                      onClick={() => setSelectedTrafficPoint(tp)}
                    />
                  ));
                })()}

              </GoogleMap>
            )}

            {/* Empty-state overlay — shown when filters hide every marker */}
            {isLoaded && markersLoaded && backupMarkerState.length > 0 && markers.filter(m => m.isvisible).length === 0 && (() => {
              const selectedDefectLabels = Object.values(checkboxes)
                .filter(cb => cb.selected && cb.id !== "0")
                .map(cb => cb.label);
              const dateLabel = selectedSurveyDate
                ? new Date(selectedSurveyDate + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                : '';
              const defectText = selectedDefectLabels.length === 0
                ? 'selected defects'
                : selectedDefectLabels.length <= 2
                  ? selectedDefectLabels.join(', ')
                  : `${selectedDefectLabels.slice(0, 2).join(', ')} +${selectedDefectLabels.length - 2} more`;
              const dateText = dateLabel ? ` on ${dateLabel}` : '';
              return (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30">
                  <div className="pointer-events-auto bg-white/95 backdrop-blur border border-gray-200 shadow-lg rounded-xl px-5 py-4 max-w-md flex items-start gap-3">
                    <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-gray-700 leading-snug">
                      No defect found for <span className="font-semibold text-gray-900">{defectText}</span>{dateText}.
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Traffic Congestion overlay card — top-left corner */}
            {selectedTrafficPoint && (() => {
              const dk = mapStyleMode === 'dark';
              return (
                <div className={`absolute top-3 left-3 z-40 rounded-xl shadow-lg border p-4 max-w-xs backdrop-blur-sm ${dk ? 'bg-slate-800/95 border-slate-700' : 'bg-white border-slate-200'}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {/* Header with close */}
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${dk ? 'bg-orange-900/40' : 'bg-orange-50'}`}>
                        <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className={`font-semibold text-sm leading-tight ${dk ? 'text-slate-100' : 'text-slate-800'}`}>
                        {selectedTrafficPoint.address || `${selectedTrafficPoint.latitude}, ${selectedTrafficPoint.longitude}`}
                      </span>
                    </div>
                    <button onClick={() => setSelectedTrafficPoint(null)} className={`p-1 rounded-md transition-colors flex-shrink-0 ${dk ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}>
                      <svg className={`w-4 h-4 ${dk ? 'text-slate-400' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>

                  {/* Badges */}
                  <div className="flex gap-1.5 mb-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${dk ? 'bg-red-900/40 text-red-400' : 'bg-red-50 text-red-600'}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> HIGH
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${dk ? 'bg-red-900/40 text-red-400' : 'bg-red-50 text-red-600'}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> HOTSPOT
                    </span>
                  </div>

                  {/* Road name */}
                  {selectedTrafficPoint.road_name && (
                    <p className={`text-xs mb-2 ${dk ? 'text-slate-400' : 'text-slate-500'}`}>Road: {selectedTrafficPoint.road_name}</p>
                  )}

                  {/* Nearby defects */}
                  {selectedTrafficPoint.defectLabels && selectedTrafficPoint.defectLabels.length > 0 ? (
                    <div>
                      <p className={`text-[10px] font-semibold uppercase tracking-wide mb-1 ${dk ? 'text-slate-500' : 'text-slate-400'}`}>Nearby Defects</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedTrafficPoint.defectLabels.map((label, i) => (
                          <span key={i} className={`text-[11px] rounded-full px-2 py-0.5 border ${dk ? 'text-slate-300 bg-slate-700/60 border-slate-600' : 'text-slate-600 bg-slate-50 border-slate-200'}`}>
                            {label.replace(/[_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className={`text-xs ${dk ? 'text-slate-500' : 'text-slate-400'}`}>No defects detected nearby</p>
                  )}

                  {/* Coordinates */}
                  <div className={`flex gap-2 mt-2 pt-2 border-t ${dk ? 'border-slate-700' : 'border-slate-100'}`}>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${dk ? 'text-slate-400 bg-slate-700/60 border-slate-600' : 'text-slate-500 bg-slate-50 border-slate-200'}`}>
                      {Number(selectedTrafficPoint.latitude).toFixed(6)}
                    </span>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${dk ? 'text-slate-400 bg-slate-700/60 border-slate-600' : 'text-slate-500 bg-slate-50 border-slate-200'}`}>
                      {Number(selectedTrafficPoint.longitude).toFixed(6)}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Bottom Defect Filter Bar */}
          <div className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none">
            {/* Toggle tab */}
            <div className="flex justify-center pointer-events-auto">
              <button
                onClick={() => setShowDefectFilters(v => !v)}
                className="bg-white border border-b-0 border-gray-200 rounded-t-lg px-4 py-1 text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1.5 shadow-sm transition-colors"
              >
                <svg className={`w-3 h-3 transition-transform ${showDefectFilters ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {showDefectFilters ? 'Hide' : 'Show'} Defect Filters
              </button>
            </div>
            {showDefectFilters && (
              <div className="bg-white border-t border-gray-200 shadow-lg pointer-events-auto">

                {/* Active filter chips row */}
                {(Object.values(severityOptions).slice(1).some(s => s.selected) || Object.values(checkboxes).some(cb => cb.selected)) && (
                  <div className="px-6 py-1.5 flex items-center gap-1.5 flex-wrap border-b border-gray-100 bg-gray-50">
                    <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                    </svg>
                    {Object.values(severityOptions).slice(1).filter(s => s.selected).map(s => (
                      <span key={s.label} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-full font-medium">
                        {s.label}
                        <button onClick={() => setSeverityOptions(prev => { const t = { ...prev }; const idx = Object.values(t).findIndex(o => o.label === s.label); if (idx >= 0) t[idx] = { ...t[idx], selected: false }; return t; })} className="hover:text-blue-900 leading-none">×</button>
                      </span>
                    ))}
                    {Object.entries(checkboxes).filter(([k, cb]) => k !== '0' && cb.selected).map(([k, cb]) => (
                      <span key={k} className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-full font-medium">
                        {cb.label}
                        <button onClick={() => setCheckboxes(prev => ({ ...prev, [k]: { ...prev[k], selected: false } }))} className="hover:text-blue-900 leading-none">×</button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="px-6 py-2.5 flex items-center gap-5 flex-wrap">
                  {/* Severity toggles */}
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Severity</span>
                    <div className="flex items-center gap-1.5">
                      {Object.values(severityOptions).map(({ label, checkedColor, color }, index) => {
                        if (index === 0) return null; // skip "Select All"
                        const isSelected = severityOptions[index].selected;
                        const dotColor = checkedColor || color;
                        return (
                          <label
                            key={index}
                            className={`flex items-center gap-1.5 cursor-pointer px-2.5 py-1 rounded-full border text-xs font-medium transition-all select-none ${isSelected
                                ? 'bg-gray-800 border-gray-800 text-white'
                                : 'border-gray-300 text-gray-600 bg-white hover:border-gray-400'
                              }`}
                          >
                            <input
                              type="checkbox"
                              className="hidden"
                              checked={isSelected}
                              onChange={(e) => handleEnroachChange(e, index)}
                            />
                            <span
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0 border border-white/40"
                              style={{ backgroundColor: dotColor }}
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-6 w-px bg-gray-200 flex-shrink-0" />

                  {/* Defect Types dropdown */}
                  <div className="relative" ref={defectDropdownRef}>
                    <button
                      onClick={() => setDefectDropdownOpen(v => !v)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-full transition-colors focus:outline-none ${Object.values(checkboxes).some(cb => cb.selected)
                          ? 'bg-blue-600 border-blue-600 text-white font-medium'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'
                        }`}
                    >
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Defect Types
                      {Object.values(checkboxes).filter(cb => cb.selected).length > 0 && (
                        <span className="bg-white text-blue-600 text-xs rounded-full px-1.5 py-0.5 leading-none font-semibold">
                          {Object.values(checkboxes).filter(cb => cb.selected).length}
                        </span>
                      )}
                      <svg className={`w-3 h-3 transition-transform ${defectDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {defectDropdownOpen && (
                      <div className="absolute bottom-full mb-1 left-0 bg-white rounded-lg shadow-xl border border-gray-200 z-50 flex flex-col" style={{ width: '900px', maxHeight: '75vh' }}>
                        {/* Search bar */}
                        <div className="p-2.5 border-b border-gray-100 flex-shrink-0">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Search defects..."
                              value={defectSearchTerm}
                              onChange={(e) => setDefectSearchTerm(e.target.value)}
                              className="w-full px-3 py-1.5 pl-8 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <svg className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            {defectSearchTerm && (
                              <button onClick={() => setDefectSearchTerm("")} className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                          {Object.values(checkboxes).some(cb => cb.selected) && (
                            <button
                              onClick={() => {
                                setCheckboxes(prev => {
                                  const cleared = {};
                                  Object.keys(prev).forEach(k => { cleared[k] = { ...prev[k], selected: false }; });
                                  return cleared;
                                });
                              }}
                              className="mt-1.5 w-full text-xs text-red-600 hover:text-red-800 hover:bg-red-50 border border-red-200 rounded-md py-1 transition-colors"
                            >
                              Clear all filters
                            </button>
                          )}
                        </div>

                        {/* 3-column layout */}
                        <div className="flex flex-1 overflow-hidden divide-x divide-gray-100">
                          {[
                            { key: 'pavement', label: 'Roadway Surface', color: '#3B82F6' },
                            { key: 'assets', label: 'Asset Management', color: '#3B82F6' },
                            { key: 'roadSafety', label: 'Road Safety', color: '#A855F7' },
                            { key: 'assetInventory', label: 'Asset Inventory', color: ASSET_INVENTORY_COLOR },
                          ].map(({ key: catKey, label: catLabel, color }) => {
                            const subGroups = categorizedCheckboxes[catKey];
                            const allItems = subGroups.flatMap(sg => sg.items);
                            const selectedCount = allItems.filter(cb => checkboxes[cb.key]?.selected).length;
                            const allSelected = allItems.length > 0 && selectedCount === allItems.length;
                            if (defectSearchTerm.trim() !== '' && allItems.length === 0) return null;
                            return (
                              <div key={catKey} className="flex-1 flex flex-col min-w-0 overflow-hidden">
                                {/* Category header */}
                                <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 flex-shrink-0">
                                  <button
                                    className="flex items-center gap-2 flex-1 text-left"
                                    onClick={() => {
                                      setCheckboxes(prev => {
                                        const updated = { ...prev };
                                        allItems.forEach(cb => {
                                          updated[cb.key] = { ...updated[cb.key], selected: !allSelected };
                                        });
                                        return updated;
                                      });
                                    }}
                                  >
                                    <div style={{
                                      width: 20, height: 20, borderRadius: '50%',
                                      border: `2px solid ${color}`,
                                      backgroundColor: allSelected ? color : selectedCount > 0 ? `${color}33` : 'transparent',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    }}>
                                      {(allSelected || selectedCount > 0) && (
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: allSelected ? 'white' : color }} />
                                      )}
                                    </div>
                                    <span style={{ color, fontWeight: 700, fontSize: '0.8125rem' }}>{catLabel}</span>
                                    {selectedCount > 0 && (
                                      <span style={{ fontSize: '0.625rem', backgroundColor: color, color: 'white', borderRadius: 9999, padding: '1px 6px', fontWeight: 600, marginLeft: 'auto', flexShrink: 0 }}>
                                        {selectedCount}
                                      </span>
                                    )}
                                  </button>
                                </div>

                                {/* Scrollable sub-categories */}
                                <div className="overflow-y-auto flex-1 px-3 py-2 space-y-3">
                                  {subGroups.length === 0 ? (
                                    <div className="text-xs text-gray-400 text-center py-4">No defect types found</div>
                                  ) : subGroups.map(({ subKey, subLabel, items }) => {
                                    const subSelectedCount = items.filter(cb => checkboxes[cb.key]?.selected).length;
                                    const subAllSelected = items.length > 0 && subSelectedCount === items.length;
                                    return (
                                      <div key={subKey}>
                                        {/* Sub-category header */}
                                        <button
                                          className="flex items-center gap-1.5 mb-1 w-full text-left"
                                          onClick={() => {
                                            setCheckboxes(prev => {
                                              const updated = { ...prev };
                                              items.forEach(cb => {
                                                updated[cb.key] = { ...updated[cb.key], selected: !subAllSelected };
                                              });
                                              return updated;
                                            });
                                          }}
                                        >
                                          <div style={{
                                            width: 13, height: 13, borderRadius: '50%',
                                            border: `2px solid ${color}`,
                                            backgroundColor: subAllSelected ? color : subSelectedCount > 0 ? `${color}33` : 'transparent',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                          }}>
                                            {subSelectedCount > 0 && (
                                              <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: subAllSelected ? 'white' : color }} />
                                            )}
                                          </div>
                                          <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#374151' }}>{subLabel}</span>
                                        </button>

                                        {/* Items with colored left border */}
                                        <div style={{ borderLeft: `2px solid ${color}`, marginLeft: 5, paddingLeft: 10 }}>
                                          {items.map((cb) => (
                                            <button
                                              key={cb.key}
                                              className="flex items-center gap-1.5 w-full text-left py-0.5 rounded hover:bg-gray-50"
                                              onClick={() => handleCheckboxChange({ target: { checked: !checkboxes[cb.key]?.selected } }, parseInt(cb.key))}
                                            >
                                              <div style={{
                                                width: 11, height: 11, borderRadius: '50%',
                                                border: `2px solid ${checkboxes[cb.key]?.selected ? color : '#D1D5DB'}`,
                                                backgroundColor: checkboxes[cb.key]?.selected ? color : 'transparent',
                                                flexShrink: 0,
                                              }} />
                                              <span style={{ fontSize: '0.6875rem', color: '#4B5563', lineHeight: 1.4 }}>{cb.label}</span>
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                          {Object.values(categorizedCheckboxes).every(arr => arr.length === 0) && (
                            <div className="flex-1 flex items-center justify-center text-xs text-slate-500 py-4">No defect types found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Clear bottom filters */}
                  {(Object.values(checkboxes).some(cb => cb.selected) || Object.values(severityOptions).slice(1).some(s => s.selected)) && (
                    <button
                      onClick={() => {
                        setCheckboxes(prev => {
                          const cleared = {};
                          Object.keys(prev).forEach(k => { cleared[k] = { ...prev[k], selected: false }; });
                          return cleared;
                        });
                        setSeverityOptions(prev => {
                          const cleared = {};
                          Object.keys(prev).forEach(k => { cleared[k] = { ...prev[k], selected: false }; });
                          return cleared;
                        });
                      }}
                      className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 border border-red-200 rounded-full transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      Clear filters
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Right panels: point modal & polyline modal */}
        {open && (
          <div
            className="relative bg-white shadow-2xl border-l border-slate-200 z-10 flex flex-col flex-shrink-0 overflow-hidden"
            style={{ width: panelWidth }}
          >
            {/* VS Code-style drag handle on left edge */}
            <div
              className="absolute left-0 top-0 bottom-0 w-1 z-20 cursor-col-resize hover:bg-blue-500 transition-colors duration-150"
              onMouseDown={(e) => {
                e.preventDefault();
                setIsPanelDragging(true);
                const startX = e.clientX;
                const startWidth = panelWidth;
                const onMove = (ev) => {
                  const delta = startX - ev.clientX;
                  setPanelWidth(Math.min(700, Math.max(280, startWidth + delta)));
                };
                const onUp = () => {
                  setIsPanelDragging(false);
                  window.removeEventListener('mousemove', onMove);
                  window.removeEventListener('mouseup', onUp);
                };
                window.addEventListener('mousemove', onMove);
                window.addEventListener('mouseup', onUp);
              }}
            />
            {/* Header */}
            <div className="bg-slate-800 px-6 py-4 flex items-center justify-between border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-base">Inspection Point</h3>
                  <p className="text-slate-400 text-xs">Detailed Analysis</p>
                </div>
              </div>
              <button onClick={handleClose} className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors">
                <Close className="text-slate-400 hover:text-white text-xl" />
              </button>
            </div>

            {/* Content */}
            {(
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-slate-50">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <LoadingScreen />
                  </div>
                ) : (
                  <div className="p-4 space-y-3">
                    {/* Image */}
                    <div className="relative rounded-lg overflow-hidden bg-slate-200 shadow-sm aspect-video">
                      <img ref={imageRef} src={selectedImageSrc} alt="Road analysis"
                        className="absolute inset-0 w-full h-full object-cover" onError={(e) => (e.target.src = "/assests/404image.svg")} />
                      {/* Full view button — top left */}
                      <div className="absolute top-3 left-3">
                        <button onClick={() => setIsImageFullView(true)} className="p-2 bg-white/90 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-800 backdrop-blur-sm rounded-lg transition-all shadow" title="Full view">
                          <svg className="w-4 h-4 text-slate-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                          </svg>
                        </button>
                      </div>
                      {/* Download button — top right */}
                      <div className="absolute top-3 right-3">
                        <button onClick={handleDownload} className="p-2 bg-white/90 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-800 backdrop-blur-sm rounded-lg transition-all shadow" title="Download">
                          <svg className="w-4 h-4 text-slate-900 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Image Type Toggle */}
                    <div className="bg-white rounded-lg p-2 shadow-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setSelectedRadio("original")}
                          className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${selectedRadio === "original"
                            ? "bg-slate-800 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                        >
                          Original
                        </button>
                        <button
                          onClick={() => setSelectedRadio("ai_analyzed")}
                          className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${selectedRadio === "ai_analyzed"
                            ? "bg-slate-800 text-white"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                        >
                          AI Analyzed
                        </button>
                      </div>
                    </div>

                    {/* Navigation Controls */}
                    <div className="bg-white rounded-lg p-3 shadow-sm">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex gap-2">
                          <button
                            onClick={goToPreviousMarker}
                            className="p-2.5 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all"
                            title="Previous marker"
                          >
                            <svg className="w-5 h-5 text-slate-700" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                          <button
                            onClick={goToNextMarker}
                            className="p-2.5 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all"
                            title="Next marker"
                          >
                            <svg className="w-5 h-5 text-slate-700" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        <button
                          onClick={toggleAutoPlay}
                          className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isAutoPlay
                            ? 'bg-amber-500 hover:bg-amber-600 text-white'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                            }`}
                        >
                          {isAutoPlay ? (
                            <>
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              Stop
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                              </svg>
                              Play
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Defect Information */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Defect Type</p>
                          {Array.isArray(info.label) && info.label.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {info.label.map((lbl, i) => (
                                <span key={i} className="inline-block text-xs font-medium text-slate-800 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1">
                                  {lbl}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm font-semibold text-slate-900">No defects detected</p>
                          )}
                        </div>
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Date Captured</p>
                        <p className="text-sm text-slate-700">{info.date_time || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Location Information */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Location</p>
                          <p className="text-sm text-slate-700 leading-snug break-words">{info.address || 'Address not available'}</p>
                        </div>
                      </div>
                      <div className="pt-3 border-t border-slate-100">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-xs text-slate-500 mb-1.5 font-medium">Latitude</p>
                            <p className="text-xs font-mono text-slate-800 bg-slate-50 px-2.5 py-1.5 rounded border border-slate-200">
                              {info.latitude || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1.5 font-medium">Longitude</p>
                            <p className="text-xs font-mono text-slate-800 bg-slate-50 px-2.5 py-1.5 rounded border border-slate-200">
                              {info.longitude || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Feedback Section - commented out (no backend logic)
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 block">Notes & Observations</label>
                    <textarea
                      placeholder="Add notes, observations, or recommendations..."
                      rows="3"
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none bg-slate-50"
                    />
                  </div>
                  */}

                    {/* Severity Analysis for this point */}
                    {(() => {
                      const currentMarker = markers[currentMarkerIndex];
                      if (!currentMarker) return null;
                      const COLOR_TO_SEVERITY = { '#DC3C3C': 'High', '#E87A00': 'Medium', '#C8C800': 'Low', '#00BB00': 'No Defects' };
                      const COLOR_PRIORITY = { '#DC3C3C': 3, '#E87A00': 2, '#C8C800': 1, '#00BB00': 0 };

                      // Group defects by their severity color (deduped, for the breakdown list below)
                      const defectsBySeverity = { '#DC3C3C': [], '#E87A00': [], '#C8C800': [] };
                      // Tally color occurrences for the overall badge. The badge is the TRUE MODE
                      // (most-frequent severity), tie-broken toward higher severity — mirroring
                      // getMarkerColor's none-state logic so the badge matches the map dot.
                      // (was: highest/most-severe color, which let a single high-severity defect
                      //  outrank many lower-severity ones.)
                      const colorCounts = {};
                      if (currentMarker.inference_info) {
                        const seen = new Set();
                        currentMarker.inference_info.forEach(inf => {
                          const color = (isBhilwaraUser
                            ? _getBhilwaraLabelColor(inf.label || '', inf.severity || '')
                            : _getLabelColor(inf.label || '')
                          ) || '#C8C800';
                          // Occurrence tally for the mode — exclude asset-inventory items (no severity)
                          if (!isAssetInventoryName(inf.label)) {
                            colorCounts[color] = (colorCounts[color] || 0) + 1;
                          }
                          // Deduped name list per severity for the breakdown display
                          const raw = mapLabel(inf.label);
                          const name = raw.replace(/[_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                          if (seen.has(name)) return;
                          seen.add(name);
                          if (defectsBySeverity[color]) defectsBySeverity[color].push(name);
                        });
                      }
                      const modeEntries = Object.entries(colorCounts);
                      const pointColor = modeEntries.length > 0
                        ? modeEntries.sort(
                          (a, b) => b[1] - a[1] || (COLOR_PRIORITY[b[0]] || 0) - (COLOR_PRIORITY[a[0]] || 0)
                        )[0][0]
                        : '#00BB00';
                      const severityLabel = COLOR_TO_SEVERITY[pointColor] || 'Unknown';

                      return (
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Severity Analysis</p>
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ backgroundColor: pointColor + '20', border: `1.5px solid ${pointColor}` }}>
                              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: pointColor }} />
                              <span className="text-xs font-bold" style={{ color: pointColor }}>{severityLabel}</span>
                            </div>
                          </div>

                          {pointColor === '#00BB00' ? (
                            <p className="text-xs text-slate-500">No defects detected at this inspection point.</p>
                          ) : (
                            <div className="space-y-2">
                              {[
                                { color: '#DC3C3C', label: 'High', items: defectsBySeverity['#DC3C3C'] },
                                { color: '#E87A00', label: 'Medium', items: defectsBySeverity['#E87A00'] },
                                { color: '#C8C800', label: 'Low', items: defectsBySeverity['#C8C800'] },
                              ].filter(g => g.items.length > 0).map(({ color, label, items }) => (
                                <div key={label}>
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                                    <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color }}>{label} ({items.length})</span>
                                  </div>
                                  <div className="flex flex-wrap gap-1 ml-3.5">
                                    {items.map(name => (
                                      <span key={name} className="text-[10px] text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">{name}</span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Legend */}
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1.5">Legend</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                              {[
                                { color: '#DC3C3C', label: 'High' },
                                { color: '#E87A00', label: 'Medium' },
                                { color: '#C8C800', label: 'Low' },
                                { color: '#00BB00', label: 'No Defects' },
                              ].map(({ color, label }) => (
                                <div key={label} className="flex items-center gap-1">
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                                  <span className="text-[10px] text-slate-500">{label}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Drag-lock overlay — keeps col-resize cursor over entire screen while resizing */}
        {isPanelDragging && (
          <div className="fixed inset-0 z-[9999]" style={{ cursor: 'col-resize' }} />
        )}


        {isPolylineModalOpen && (
          <div className="absolute left-72 top-0 bottom-0 w-[400px] bg-white shadow-2xl border-r border-slate-200 z-50 flex flex-col">
            {/* Header */}
            <div className="bg-slate-800 px-6 py-4 flex items-center justify-between border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12 1.586l-4 4v12.828l4-4V1.586zM3.707 3.293A1 1 0 002 4v10a1 1 0 00.293.707L6 18.414V5.586L3.707 3.293zM17.707 5.293L14 1.586v12.828l2.293 2.293A1 1 0 0018 16V6a1 1 0 00-.293-.707z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-base">Road Segment</h3>
                  <p className="text-slate-400 text-xs">Overall Analysis</p>
                </div>
              </div>
              <button onClick={() => setPolylineModalOpen(false)} className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors">
                <Close className="text-slate-400 hover:text-white text-xl" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-slate-50">
              <div className="p-4 space-y-3">
                {/* RoadVision Score */}
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-center">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold text-base ${selectedPolylineData?.roadRating <= 40 ? 'bg-red-600' :
                      selectedPolylineData?.roadRating <= 80 ? 'bg-orange-500' :
                        'bg-emerald-600'
                      }`}>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Score: {selectedPolylineData?.roadRating || 0}
                    </div>
                  </div>
                  <div className="mt-3">
                    <RoadRatingGauge rating={selectedPolylineData?.roadRating} />
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-1 gap-3">
                  {/* Road Condition */}
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedPolylineData?.roadCondition === "Poor" ? "bg-red-50" :
                        selectedPolylineData?.roadCondition === "Fair" ? "bg-orange-50" :
                          "bg-emerald-50"
                        }`}>
                        <svg className={`w-5 h-5 ${selectedPolylineData?.roadCondition === "Poor" ? "text-red-600" :
                          selectedPolylineData?.roadCondition === "Fair" ? "text-orange-600" :
                            "text-emerald-600"
                          }`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">Condition</p>
                        <p className="text-base font-semibold text-slate-900">{selectedPolylineData?.roadCondition || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Road Length */}
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">Length</p>
                        <p className="text-base font-semibold text-slate-900">{selectedPolylineData?.roadLength || 0} km</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Details */}
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  {/* Starting Point */}
                  <div className="mb-4 pb-4 border-b border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Start Point</p>
                    </div>
                    <p className="text-sm text-slate-700 leading-snug mb-2">{selectedPolylineData?.startingAddress || 'N/A'}</p>
                    <div className="flex gap-2 text-xs">
                      <span className="bg-slate-100 px-2.5 py-1 rounded font-mono text-slate-700 border border-slate-200">
                        {selectedPolylineData?.entryPoint?.lat?.toFixed(6)}
                      </span>
                      <span className="bg-slate-100 px-2.5 py-1 rounded font-mono text-slate-700 border border-slate-200">
                        {selectedPolylineData?.entryPoint?.lng?.toFixed(6)}
                      </span>
                    </div>
                  </div>

                  {/* Ending Point */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">End Point</p>
                    </div>
                    <p className="text-sm text-slate-700 leading-snug mb-2">{selectedPolylineData?.endingAddress || 'N/A'}</p>
                    <div className="flex gap-2 text-xs">
                      <span className="bg-slate-100 px-2.5 py-1 rounded font-mono text-slate-700 border border-slate-200">
                        {selectedPolylineData?.exitPoint?.lat?.toFixed(6)}
                      </span>
                      <span className="bg-slate-100 px-2.5 py-1 rounded font-mono text-slate-700 border border-slate-200">
                        {selectedPolylineData?.exitPoint?.lng?.toFixed(6)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Dashboard-wide fullscreen image lightbox — covers nav/map/panel */}
      {isImageFullView && (
        <div
          className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center"
          onClick={() => setIsImageFullView(false)}
        >
          <img
            src={selectedImageSrc}
            alt="Road analysis full view"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => (e.target.src = "/assests/404image.svg")}
          />
          {/* Close (X) — top right */}
          <button
            onClick={() => setIsImageFullView(false)}
            className="absolute top-4 right-4 p-2 bg-white/90 hover:bg-white rounded-full transition-all shadow-lg"
            title="Close"
          >
            <svg className="w-5 h-5 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          {/* Download — top left, matches the non-fullscreen affordance */}
          <button
            onClick={(e) => { e.stopPropagation(); handleDownload(); }}
            className="absolute top-4 left-4 p-2 bg-white/90 hover:bg-white rounded-lg transition-all shadow-lg"
            title="Download"
          >
            <svg className="w-5 h-5 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default MultiMap;
